import { useState, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { usePrintSettings } from "@/contexts/PrintSettingsContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format as formatDate, parseISO } from "date-fns";
import FilterPanel from "@/components/shipment-costing/FilterPanel";
import SummaryCards from "@/components/shipment-costing/SummaryCards";
import DateViewSelector from "@/components/shipment-costing/DateViewSelector";
import MonthlyChart from "@/components/shipment-costing/MonthlyChart";
import UploadSummaryDialog from "@/components/shipment-costing/UploadSummaryDialog";
import ProductSuggestButton from "@/components/shipment-costing/ProductSuggestButton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Upload,
  Download,
  FileSpreadsheet,
  Package,
  Calculator,
  Truck,
  AlertTriangle,
  CheckCircle2,
  X,
  ChevronLeft,
  ChevronRight,
  Search,
  Filter,
  Printer,
  RefreshCw,
  DollarSign,
  Scale,
  Loader2,
  FileText,
  Trash2,
  ShieldAlert,
  ShieldCheck,
  Lock,
  FileBox,
  Weight,
  Settings2,
  Eye,
  Calendar as CalendarIcon,
  List,
  EyeOff,
  Clock,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { formatDMY } from "@/lib/dmy";
import InlineRowEditor from "@/components/shipment-costing/InlineRowEditor";
import RowEditorDialog from "@/components/shipment-costing/RowEditorDialog";
import EditHistoryDialog from "@/components/shipment-costing/EditHistoryDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ITEMS_PER_PAGE = 50;
const SERVER_PAGE_SIZE = 500; // Server-side pagination size

const ShipmentCostingPage = () => {
  usePageTitle("Shipment Costing");
  const { token, user } = useAuth();
  const { settings: printSettings } = usePrintSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadTime, setLoadTime] = useState(null); // Track API response time in ms
  // Progress card for the long-running "Recalculate All" job. Shape:
  //   { job_id, phase, processed, total, updated, cancelled, message, done }
  const [recalcProgress, setRecalcProgress] = useState(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadSummaryResult, setUploadSummaryResult] = useState(null);
  const [showWipeDialog, setShowWipeDialog] = useState(false);
  // Duplicate-tracking report
  const [showDuplicateTrackingDialog, setShowDuplicateTrackingDialog] = useState(false);
  const [dupReport, setDupReport] = useState(null);
  const [dupReportLoading, setDupReportLoading] = useState(false);
  const [dupReportError, setDupReportError] = useState("");
  const [dupReportScope, setDupReportScope] = useState({ month: "", year: "" });
  const [dupIncludeResolved, setDupIncludeResolved] = useState(false);
  const [dupResolvingTracking, setDupResolvingTracking] = useState("");
  // Lightweight counts shown in the More menu entry, refreshed on page load
  // and whenever the dialog closes (so fresh resolutions are reflected).
  const [dupCounts, setDupCounts] = useState({ open: 0, resolved: 0 });

  const fetchDuplicateTrackingCounts = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/shipment-costing/duplicate-tracking-report?include_resolved=true&limit=2000`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const open = (data.groups || []).filter((g) => !g.resolved).length;
      const resolved = (data.groups || []).filter((g) => g.resolved).length;
      setDupCounts({ open, resolved });
    } catch {
      // Silent -- this is a background refresh
    }
  };

  const fetchDuplicateTrackingReport = async (scope = dupReportScope) => {
    setDupReportLoading(true);
    setDupReportError("");
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      if (scope.month) params.set("month", String(scope.month).padStart(2, "0"));
      if (scope.year) params.set("year", String(scope.year));
      if (dupIncludeResolved) params.set("include_resolved", "true");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/shipment-costing/duplicate-tracking-report${params.toString() ? `?${params.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Failed to fetch duplicate-tracking report");
      }
      const data = await res.json();
      setDupReport(data);
    } catch (e) {
      setDupReportError(e.message || "Could not load report");
      setDupReport(null);
    } finally {
      setDupReportLoading(false);
    }
  };

  useEffect(() => {
    if (showDuplicateTrackingDialog && !dupReport) {
      fetchDuplicateTrackingReport();
    }
    // Refresh counts when closing the dialog (user may have resolved some)
    if (!showDuplicateTrackingDialog) {
      fetchDuplicateTrackingCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDuplicateTrackingDialog]);

  // Initial counts fetch on mount
  useEffect(() => {
    fetchDuplicateTrackingCounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when the include-resolved toggle flips
  useEffect(() => {
    if (showDuplicateTrackingDialog) {
      fetchDuplicateTrackingReport(dupReportScope);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dupIncludeResolved]);

  const markDuplicateResolved = async (tracking, note = "") => {
    setDupResolvingTracking(tracking);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/shipment-costing/duplicate-tracking-report/resolve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ tracking, note }),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Resolve failed");
      toast.success(`${tracking} marked as resolved`);
      await fetchDuplicateTrackingReport(dupReportScope);
    } catch (e) {
      toast.error(e.message || "Could not resolve");
    } finally {
      setDupResolvingTracking("");
    }
  };

  const unmarkDuplicateResolved = async (tracking) => {
    setDupResolvingTracking(tracking);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/shipment-costing/duplicate-tracking-report/resolve/${encodeURIComponent(tracking)}`,
        { method: "DELETE", headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Un-resolve failed");
      toast.success(`${tracking} re-opened`);
      await fetchDuplicateTrackingReport(dupReportScope);
    } catch (e) {
      toast.error(e.message || "Could not un-resolve");
    } finally {
      setDupResolvingTracking("");
    }
  };
  const [wipeOption, setWipeOption] = useState("today");
  const [wiping, setWiping] = useState(false);
  const [wipeStep, setWipeStep] = useState(1); // 1: confirm, 2: password
  const [wipePassword, setWipePassword] = useState("");
  const [wipePasswordError, setWipePasswordError] = useState("");
  // Date-range / Month wipe parameters + server-side preview of affected rows.
  const [wipeDateFrom, setWipeDateFrom] = useState("");
  const [wipeDateTo, setWipeDateTo] = useState("");
  const [wipeMonth, setWipeMonth] = useState(new Date().getMonth() + 1);
  const [wipeYear, setWipeYear] = useState(new Date().getFullYear());
  const [wipeIncludeSales, setWipeIncludeSales] = useState(false);
  const [wipePreview, setWipePreview] = useState(null);
  const [wipePreviewLoading, setWipePreviewLoading] = useState(false);
  const [wipePreviewError, setWipePreviewError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [serverPage, setServerPage] = useState(1);
  const [serverPagination, setServerPagination] = useState({ total_rows: 0, total_pages: 0, has_more: false });
  const [searchTerm, setSearchTerm] = useState("");
  const [serverSearchTerm, setServerSearchTerm] = useState(""); // For server-side search (tracking number)
  // Pending value the user is typing into the search box. The auto-fetch
  // useEffect listens to `serverSearchTerm` (the COMMITTED value) so the
  // server only refetches when the user explicitly clicks Load / Enter /
  // ✕ clear. This stops the page from issuing a network round-trip on
  // every keystroke.
  const [pendingSearchTerm, setPendingSearchTerm] = useState("");
  const [filterErrors, setFilterErrors] = useState(false);
  const [filterSuccessOnly, setFilterSuccessOnly] = useState(false);  // New: filter for successful only
  const [filterNotifications, setFilterNotifications] = useState(false);  // NEW: filter for notifications
  const [filterNotificationType, setFilterNotificationType] = useState("");  // NEW: specific notification type
  const [filterReplacedTracking, setFilterReplacedTracking] = useState(false);  // NEW: filter for replaced tracking numbers
  const [filterWithoutDate, setFilterWithoutDate] = useState(false);  // NEW: filter for orders without dates
  const [filterAutoAssignedDate, setFilterAutoAssignedDate] = useState(false);  // NEW: filter for orders with auto-assigned dates
  const [sheetsProcessed, setSheetsProcessed] = useState([]);
  
  // Advanced Filters
  const [filterService, setFilterService] = useState("");
  const [filterCountry, setFilterCountry] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterCostMin, setFilterCostMin] = useState("");
  const [filterErrorType, setFilterErrorType] = useState("");  // New: specific error type filter
  const [filterDateFrom, setFilterDateFrom] = useState(() => {
    // If the page is being opened via a tracking deep-link, start with NO
    // date filter so the very first fetch runs globally. Otherwise default
    // to the current month start.
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("tracking")) {
      return "";
    }
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth(), 1);
    return `${String(d.getDate()).padStart(2,'0')}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getFullYear()).slice(-2)}`;
  });
  const [filterDateTo, setFilterDateTo] = useState(() => {
    if (typeof window !== "undefined" && new URLSearchParams(window.location.search).has("tracking")) {
      return "";
    }
    const now = new Date();
    const d = new Date(now.getFullYear(), now.getMonth() + 1, 0); // last day of current month
    return `${String(d.getDate()).padStart(2,'0')}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getFullYear()).slice(-2)}`;
  });
  const [filterDpClientsOnly, setFilterDpClientsOnly] = useState(false);  // NEW: DP CLIENTS only filter
  const [dpClientCodes, setDpClientCodes] = useState([]);  // Store DP CLIENT codes
  const [allLedgerClients, setAllLedgerClients] = useState([]);  // Store ALL client codes from ledgers
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showOrderSheetErrors, setShowOrderSheetErrors] = useState(false);  // Default: hide order sheet errors (for learning only)
  const [showCustomerColumn, setShowCustomerColumn] = useState(false);  // Hidden customer name column toggle
  const [fixingTrackingReplacements, setFixingTrackingReplacements] = useState(false);  // Tracking replacement fix in progress
  
  // Date conflict dialog state
  const [showDateConflictDialog, setShowDateConflictDialog] = useState(false);
  const [dateConflictData, setDateConflictData] = useState(null);
  const [pendingUploadFile, setPendingUploadFile] = useState(null);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateData, setDuplicateData] = useState(null);
  const [pendingNewOrders, setPendingNewOrders] = useState([]);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [overwritePassword, setOverwritePassword] = useState("");
  
  // Background processing state (for large files)
  const [backgroundTaskId, setBackgroundTaskId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadProgressMessage, setUploadProgressMessage] = useState("");
  
  // Packing List state
  const [packingListData, setPackingListData] = useState(null); // Parsed packing list indexed by tracking
  const [packingListFile, setPackingListFile] = useState(null); // File name for display
  const [uploadingPacking, setUploadingPacking] = useState(false);
  const [weightSource, setWeightSource] = useState("price_list"); // "price_list" or "packing_list"
  const [showWeightSourceDialog, setShowWeightSourceDialog] = useState(false);
  
  // DP Client Auto Sales Generation state
  const [generatingDPSales, setGeneratingDPSales] = useState(false);
  const [dpSalesResult, setDpSalesResult] = useState(null);
  const [showDPSalesDialog, setShowDPSalesDialog] = useState(false);

  // Captures whether the page was opened with `?tracking=` so the
  // DateViewSelector child can skip its default current-month fire.
  // We use a ref because the URL params are cleared synchronously after
  // we apply them — but the date selector's mount effect runs after that.
  const arrivedWithTracking = useRef(
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("tracking"),
  );

  // ---- INLINE EDIT STATE ----
  // The row currently open in the centered editor dialog (null if none).
  const [editingRow, setEditingRow] = useState(null);
  // cached admin password (valid for the lifetime of the page; cleared on
  // refresh). Set once the user enters it correctly via the password gate.
  const [editAdminPwd, setEditAdminPwd] = useState("");
  // pending password gate — the row waiting for password verification.
  const [pendingEditRow, setPendingEditRow] = useState(null);
  const [editPwdInput, setEditPwdInput] = useState("");
  const [editPwdVerifying, setEditPwdVerifying] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  // legacy (unused but kept to avoid widespread refactor):
  const [editingRecordId, setEditingRecordId] = useState(null);
  // dropdown lookups for the editor
  const [editLookups, setEditLookups] = useState({ clients: [], services: [], countries: [] });

  // Fetch lookups once for the editor (clients, services, countries from
  // existing endpoints). Stays in component state so dropdowns are snappy.
  useEffect(() => {
    if (!token) return;
    const ctrl = new AbortController();
    const load = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [cliRes, sRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/errors/client-list`, { headers, signal: ctrl.signal }).catch(() => null),
          fetch(`${BACKEND_URL}/api/shipping/paginated?page=1&page_size=1`, { headers, signal: ctrl.signal }).catch(() => null),
        ]);
        let clients = [];
        if (cliRes && cliRes.ok) {
          const d = await cliRes.json();
          const arr = Array.isArray(d) ? d : (d.clients || d.items || []);
          clients = arr
            .map((c) => (typeof c === "string" ? c : c.client_code || c.code || c.client || ""))
            .map((s) => (s || "").toUpperCase())
            .filter(Boolean);
        }
        let services = [];
        let countries = [];
        if (sRes && sRes.ok) {
          const data = await sRes.json();
          // Prefer the server-computed distinct lists in `filters`; fall
          // back to mapping rows (which may be page-limited).
          const filters = data.filters || {};
          services = (filters.shipments || []).filter(Boolean);
          countries = (filters.countries || []).filter(Boolean);
          if (services.length === 0) {
            const rates = data.shipping || data.items || [];
            services = Array.from(new Set(rates.map((r) => r.shipment).filter(Boolean))).sort();
          }
          if (countries.length === 0) {
            const rates = data.shipping || data.items || [];
            countries = Array.from(new Set(rates.map((r) => r.country).filter(Boolean))).sort();
          }
        }
        setEditLookups({
          clients: Array.from(new Set(clients)).sort(),
          services,
          countries,
        });
      } catch (e) {
        if (e.name !== "AbortError") console.warn("lookup fetch failed:", e);
      }
    };
    load();
    return () => ctrl.abort();
  }, [token]);

  // Product autocomplete — calls the existing product search endpoint.
  const onEditProductSearch = async (q) => {
    try {
      const r = await fetch(
        `${BACKEND_URL}/api/products?search=${encodeURIComponent(q)}&limit=8`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!r.ok) return [];
      const d = await r.json();
      const arr = Array.isArray(d) ? d : (d.products || d.items || []);
      return arr
        .map((p) => p.product_name || p.name || p.second_name)
        .filter(Boolean)
        .slice(0, 8);
    } catch {
      return [];
    }
  };

  // Triggered on double-click on any editable cell.
  const fetchPersistedDataRef = useRef(null);
  const rowKey = (order) => {
    // Prefer the backend's UUID; fall back to the composite key the new
    // shipment_costing_edit route understands (`sheet|row#|tracking`).
    if (order?.record_id) return order.record_id;
    if (order?._id) return order._id;
    if (order?.id) return order.id;
    if (order?.sheet_name && order?.row_number != null && order?.tracking) {
      return `${order.sheet_name}|${order.row_number}|${order.tracking}`;
    }
    return null;
  };

  const handleEditTrigger = (order) => {
    if (!order) return;
    // Defensive: force any focused page input (search box, etc.) to blur
    // so the user's keystrokes can't leak into them while the modal opens.
    try {
      const el = document.activeElement;
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA")) el.blur();
      const sel = window.getSelection?.();
      sel?.removeAllRanges?.();
    } catch {}
    if (editAdminPwd) {
      // Already verified earlier in this session — open editor directly.
      setEditingRow(order);
      return;
    }
    setPendingEditRow(order);
    setEditPwdInput("");
  };

  const verifyEditPassword = async () => {
    if (!editPwdInput) return;
    setEditPwdVerifying(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/shipment-costing/verify-admin`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ admin_password: editPwdInput }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${r.status}`);
      }
      setEditAdminPwd(editPwdInput);
      setEditingRow(pendingEditRow);
      setPendingEditRow(null);
      setEditPwdInput("");
    } catch (e) {
      toast.error(`Verification failed: ${e.message}`);
    } finally {
      setEditPwdVerifying(false);
    }
  };

  // Called when a row save succeeds — refresh the table data and close.
  const handleRowSaved = () => {
    setEditingRow(null);
    // Reliable, page-wide refresh (also bypasses the ref-based debounced
    // fetcher used by the search effect).
    handleRefresh();
  };
  
  // Handle URL parameters for filtering from Dashboard / Reconcile drill-down
  useEffect(() => {
    const filter = searchParams.get('filter');
    const trackingParam = searchParams.get('tracking');
    if (filter === 'errors') {
      setFilterErrors(true);
      setFilterSuccessOnly(false);
      setShowFilterPanel(true);
    } else if (filter === 'success') {
      setFilterSuccessOnly(true);
      setFilterErrors(false);
      setShowFilterPanel(true);
    }
    if (trackingParam) {
      setSearchTerm(trackingParam);
      setServerSearchTerm(trackingParam);
      setPendingSearchTerm(trackingParam);
      // Clear the default current-month date window so the search runs
      // globally — tracking numbers may live in any prior month/year.
      setFilterDateFrom("");
      setFilterDateTo("");
    }
    // Clear the URL parameter after applying
    if (filter || trackingParam) {
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Print options
  const [printFilters, setPrintFilters] = useState({
    errorsOnly: false,
    service: "",
    country: "",
    client: "",
    costMin: ""
  });
  // Default print column set tuned for the finance team's preferred Order
  // Cost Sheet printout: keep Client/Date/Tracking/Product/Qty/Med Cost/Ship
  // Cost/Total visible; drop Service/Customer/Country/Weight/Errors from the
  // default selection (still toggleable in the dialog).
  // Persist last-used selection per user via localStorage so finance can
  // fine-tune once and have it auto-applied every session.
  const PRINT_PREF_KEY = "eximflow.shipmentCosting.printPrefs.v1";
  const PRINT_DEFAULTS = {
    columns: {
      client: true,
      tracking: true,
      product: true,
      qty: true,
      service: false,
      country: false,
      medicineCost: true,
      weight: false,
      shippingCost: true,
      total: true,
      errors: false,
      customerName: false,
      date: true,
    },
    showCompanyName: true,
    showHeader: true,
    showSummary: false,
  };
  const loadPrintPrefs = () => {
    try {
      const raw = localStorage.getItem(PRINT_PREF_KEY);
      if (!raw) return PRINT_DEFAULTS;
      const parsed = JSON.parse(raw);
      return {
        columns: { ...PRINT_DEFAULTS.columns, ...(parsed.columns || {}) },
        showCompanyName: parsed.showCompanyName ?? PRINT_DEFAULTS.showCompanyName,
        showHeader: parsed.showHeader ?? PRINT_DEFAULTS.showHeader,
        showSummary: parsed.showSummary ?? PRINT_DEFAULTS.showSummary,
      };
    } catch {
      return PRINT_DEFAULTS;
    }
  };
  const _initialPrintPrefs = loadPrintPrefs();
  const [printColumns, setPrintColumns] = useState(_initialPrintPrefs.columns);
  const [printShowCompanyName, setPrintShowCompanyName] = useState(_initialPrintPrefs.showCompanyName);
  const [printShowHeader, setPrintShowHeader] = useState(_initialPrintPrefs.showHeader);
  const [printShowSummary, setPrintShowSummary] = useState(_initialPrintPrefs.showSummary);
  useEffect(() => {
    try {
      localStorage.setItem(
        PRINT_PREF_KEY,
        JSON.stringify({
          columns: printColumns,
          showCompanyName: printShowCompanyName,
          showHeader: printShowHeader,
          showSummary: printShowSummary,
        })
      );
    } catch {
      // localStorage might be full or unavailable (private mode) — silently ignore.
    }
  }, [printColumns, printShowCompanyName, printShowHeader, printShowSummary]);
  
  // Get unique values for filter dropdowns
  const uniqueServices = useMemo(() => {
    const services = [...new Set(orders.map(o => o.detected_service).filter(Boolean))];
    return services.sort();
  }, [orders]);
  
  const uniqueCountries = useMemo(() => {
    const countries = [...new Set(orders.map(o => o.detected_country).filter(Boolean))];
    return countries.sort();
  }, [orders]);
  
  const uniqueClients = useMemo(() => {
    const clients = [...new Set(orders.map(o => o.client_code).filter(Boolean))];
    return clients.sort();
  }, [orders]);
  
  // Get unique error types with counts for filter dropdown
  const uniqueErrorTypesWithCounts = useMemo(() => {
    const errorCounts = {};
    orders.forEach(o => {
      if (o.errors && o.errors.length > 0) {
        o.errors.forEach(err => {
          // Normalize error type (remove product names from "Product not found: X")
          const normalizedErr = err.startsWith('Product not found:') ? 'Product not found' : err;
          errorCounts[normalizedErr] = (errorCounts[normalizedErr] || 0) + 1;
        });
      }
    });
    // Convert to array of { type, count } and sort by type name
    return Object.entries(errorCounts)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => a.type.localeCompare(b.type));
  }, [orders]);
  
  // Keep uniqueErrorTypes for backwards compatibility (just the type names)
  const uniqueErrorTypes = useMemo(() => {
    return uniqueErrorTypesWithCounts.map(e => e.type);
  }, [uniqueErrorTypesWithCounts]);
  
  // NEW: Get unique notification types with counts
  const uniqueNotificationTypesWithCounts = useMemo(() => {
    const notifCounts = {};
    orders.forEach(o => {
      if (o.notifications && o.notifications.length > 0) {
        o.notifications.forEach(notif => {
          // Normalize notification type 
          let normalizedNotif = notif;
          // Group all "Weight exceeds" messages together
          if (notif.includes('Weight') && notif.includes('exceeds')) {
            normalizedNotif = 'Weight exceeds max slab';
          }
          // Group all "Order shifted" messages
          else if (notif.includes('Order shifted')) {
            normalizedNotif = 'Order shifted';
          }
          notifCounts[normalizedNotif] = (notifCounts[normalizedNotif] || 0) + 1;
        });
      }
    });
    // Convert to array of { type, count } and sort by count descending
    return Object.entries(notifCounts)
      .map(([type, count]) => ({ type, count }))
      .sort((a, b) => b.count - a.count);
  }, [orders]);
  
  // Total notification count
  const totalNotificationCount = useMemo(() => {
    return orders.filter(o => o.notifications && o.notifications.length > 0).length;
  }, [orders]);
  
  // Replaced tracking count
  const replacedTrackingCount = useMemo(() => {
    return orders.filter(o => o.is_strikethrough === true || o.superseded_by).length;
  }, [orders]);
  
  // Without date count - orders that don't have a date
  const withoutDateCount = useMemo(() => {
    return orders.filter(o => !o.order_date || o.order_date === "" || o.order_date === null).length;
  }, [orders]);
  
  // Auto-assigned date count - orders that had dates auto-assigned
  const autoAssignedDateCount = useMemo(() => {
    return orders.filter(o => o.date_auto_assigned === true).length;
  }, [orders]);
  
  // Helper function to get REAL errors (excluding informational errors when calculation succeeded)
  // These are NOT real errors if the calculation was successful:
  // - "Product not found" / "Price Missing" - product was found from packing sheet
  // - "Shipping Rate Missing" / "Shipping rate not found" - shipping was calculated from packing sheet weight
  const getRealErrors = (order) => {
    if (!order.errors || order.errors.length === 0) return [];
    
    // Check if calculation was successful
    const hasMedicineCost = order.medicine_cost && order.medicine_cost > 0;
    const hasShippingCost = order.shipping_cost && order.shipping_cost > 0;
    const hasTotalCost = order.total_cost && order.total_cost > 0;
    
    // Informational error patterns (NOT real errors if corresponding calculation succeeded)
    const productErrorPatterns = [
      'product not found',
      'price missing',
      'not found in price list'
    ];
    
    const shippingErrorPatterns = [
      'shipping rate missing',
      'shipping rate not found',
      'shipping not found'
    ];
    
    // Filter: Keep only REAL errors
    const realErrors = order.errors.filter(err => {
      const errLower = err.toLowerCase();
      
      // If it's a product error AND medicine cost is calculated → NOT a real error
      const isProductError = productErrorPatterns.some(pattern => errLower.includes(pattern));
      if (isProductError && hasMedicineCost) {
        return false;
      }
      
      // If it's a shipping error AND shipping cost is calculated → NOT a real error
      const isShippingError = shippingErrorPatterns.some(pattern => errLower.includes(pattern));
      if (isShippingError && hasShippingCost) {
        return false;
      }
      
      return true;
    });
    
    return realErrors;
  };
  
  // Helper function to get display errors (respects the showOrderSheetErrors toggle)
  const getDisplayErrors = (order) => {
    if (!order.errors || order.errors.length === 0) return [];
    
    // If "Order Sheet Errors" button is ON, show all for learning
    if (showOrderSheetErrors) return order.errors;
    
    // Otherwise, show only real errors (filter out informational when calculation succeeded)
    return getRealErrors(order);
  };
  
  // Calculate REAL error count (excluding order sheet informational errors)
  const realErrorCount = useMemo(() => {
    return orders.filter(o => getRealErrors(o).length > 0).length;
  }, [orders]);

  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { rowIdx: number, field: string }
  const [editValue, setEditValue] = useState("");

  // Check if user is super admin
  const isSuperAdmin = user?.username === "admin";

  // Handle double-click to edit cell
  const handleCellDoubleClick = (rowIdx, field, currentValue) => {
    setEditingCell({ rowIdx, field });
    setEditValue(currentValue || "");
  };

  // Handle edit save with auto-fetch product prices AND save to database
  const handleEditSave = async (rowIdx, field) => {
    if (!editingCell) return;
    
    // Find the target order
    const filteredIdx = ((currentPage - 1) * ITEMS_PER_PAGE) + rowIdx;
    const targetOrder = filteredOrders[filteredIdx];
    
    if (!targetOrder) {
      setEditingCell(null);
      setEditValue("");
      return;
    }
    
    const actualIdx = orders.findIndex(o => 
      o.row_number === targetOrder.row_number && 
      o.tracking === targetOrder.tracking
    );
    
    if (actualIdx === -1) {
      setEditingCell(null);
      setEditValue("");
      return;
    }
    
    // Update the order locally first
    const updatedOrder = { ...orders[actualIdx], [field]: editValue };
    
    // If editing product name, fetch new prices from backend
    if (field === 'second_name' && editValue !== targetOrder.second_name) {
      try {
        toast.info("Fetching product prices...");
        
        const response = await fetch(`${BACKEND_URL}/api/shipment-costing/lookup-product`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            product_name: editValue,
            qty: updatedOrder.qty || 1
          })
        });
        
        if (response.ok) {
          const priceData = await response.json();
          
          if (priceData.found) {
            updatedOrder.medicine_cost = priceData.medicine_cost;
            updatedOrder.product_weight = priceData.product_weight;
            updatedOrder.matched_product = priceData.matched_product;
            
            // Remove price-related errors if product is now found
            updatedOrder.errors = (updatedOrder.errors || []).filter(
              e => !e.includes('Price Missing') && !e.includes('Product not found') && !e.includes('Weight Not Found')
            );
            
            toast.success(`Product found! Price: ₹${priceData.medicine_cost}, Weight: ${priceData.product_weight}g`);
          } else {
            toast.warning("Product not found in price list");
          }
        }
      } catch (error) {
        console.error("Failed to lookup product:", error);
        toast.error("Failed to fetch product prices");
      }
    }
    
    // Update local state
    setOrders(prevOrders => {
      const newOrders = [...prevOrders];
      newOrders[actualIdx] = updatedOrder;
      return newOrders;
    });
    
    // SAVE TO DATABASE - so changes persist on refresh
    try {
      const saveResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/update-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          row_number: targetOrder.row_number,
          tracking: targetOrder.tracking,
          sheet_name: targetOrder.sheet_name,
          updates: {
            [field]: editValue,
            medicine_cost: updatedOrder.medicine_cost,
            product_weight: updatedOrder.product_weight,
            matched_product: updatedOrder.matched_product,
            errors: updatedOrder.errors
          }
        })
      });
      
      if (saveResponse.ok) {
        toast.success("Changes saved to database");
      } else {
        toast.warning("Local update done, but failed to save to database");
      }
    } catch (error) {
      console.error("Failed to save to database:", error);
      toast.warning("Local update done, but failed to save to database");
    }
    
    setEditingCell(null);
    setEditValue("");
  };

  // Refresh data from backend
  const handleRefresh = async () => {
    try {
      setLoadingData(true);
      toast.info("Refreshing data...");
      
      // Build query params for server-side pagination
      const params = new URLSearchParams();
      params.set('page', serverPage.toString());
      params.set('page_size', SERVER_PAGE_SIZE.toString());
      if (serverSearchTerm && serverSearchTerm.length >= 3) {
        params.set('search', serverSearchTerm);
      }
      if (filterClient) {
        params.set('client', filterClient);
      }
      // Server-side date filtering
      if (filterDateFrom) {
        params.set('date_from', filterDateFrom);
      }
      if (filterDateTo) {
        params.set('date_to', filterDateTo);
      }
      
      const response = await fetch(`${BACKEND_URL}/api/shipment-costing/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
        setSummary(data.summary);
        setSheetsProcessed(data.sheets_processed || []);
        // Store server pagination info
        if (data.pagination) {
          setServerPagination(data.pagination);
        }
        toast.success("Data refreshed successfully");
      } else {
        toast.error("Failed to refresh data");
      }
    } catch (error) {
      console.error("Refresh error:", error);
      toast.error("Failed to refresh data");
    } finally {
      setLoadingData(false);
    }
  };

  // Recalculate all prices (re-fetch from product database) — background job
  // with live progress polling. Falls back to the old sync flow if the job
  // fails to start.
  const handleRecalculatePrices = async (opts = {}) => {
    if (recalcProgress && !recalcProgress.done) {
      toast.info("Recalculation already running");
      return;
    }
    const jobId = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `job_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    // Optional client-scoped recalc: pass {clientCode: "BHA"} to only sweep that client.
    const clientCode = opts.clientCode ? String(opts.clientCode).trim().toUpperCase() : "";

    try {
      setRecalcProgress({ job_id: jobId, phase: "starting", processed: 0, total: 0, updated: 0, cancelled: 0, done: false, client_code: clientCode || null });
      toast.info(clientCode
        ? `Recalculating costs for ${clientCode} — you can keep working while this runs`
        : "Recalculating all costs — you can keep working while this runs");

      // Kick off the price-recalc in background mode
      const priceStart = await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-all`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ job_id: jobId, ...(clientCode ? { client_code: clientCode } : {}) }),
      });
      if (!priceStart.ok) {
        setRecalcProgress(null);
        toast.error("Failed to start recalculation");
        return;
      }

      // Poll the progress endpoint every 1.5s until done. When done, fire the
      // usual shipping-recalc + cleanup pass (those two are much faster and
      // don't need a progress bar), then refresh the orders list.
      const poll = async () => {
        try {
          const r = await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-all/progress/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!r.ok) return;
          const data = await r.json();
          if (!data.found) return;
          setRecalcProgress({ job_id: jobId, ...data });
          if (data.done) {
            clearInterval(pollHandle);
            if (data.phase === 'cancelled' || data.cancelled_by_user) {
              toast.info(data.message || "Recalculation cancelled");
              // Skip the follow-up shipping recalc + cleanup on cancel — the
              // user asked to stop. Keep the card visible longer so they see
              // the partial-summary message.
              setTimeout(() => setRecalcProgress(null), 8000);
            } else {
              await finishRecalc(data);
            }
          }
        } catch (e) {
          // Transient network / 502 during poll — quietly ignore, next tick retries
        }
      };
      const pollHandle = setInterval(poll, 1500);
      // Kick off immediately so the UI doesn't wait 1.5s for the first tick
      poll();
    } catch (error) {
      console.error("Recalculate error:", error);
      toast.error("Failed to recalculate prices");
      setRecalcProgress(null);
    }
  };

  const cancelRecalc = async () => {
    const jobId = recalcProgress?.job_id;
    if (!jobId) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-all/cancel/${jobId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        toast.info("Cancel requested — will stop at the next checkpoint");
      } else {
        toast.error("Cancel failed");
      }
    } catch (e) {
      toast.error("Cancel failed");
    }
  };

  // Runs the follow-up steps (shipping recalc, error cleanup, refresh) once
  // the background price-recalc job reports done.
  const finishRecalc = async (finalPriceData) => {
    try {
      const priceUpdated = finalPriceData.updated || 0;
      let shipUpdated = 0;

      const shipResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-shipping`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });
      if (shipResponse.ok) {
        const shipData = await shipResponse.json();
        shipUpdated = shipData.updated_count || 0;
        if (shipData.fixed_shipping?.length) {
          toast.info(`Fixed shipping: ${shipData.fixed_shipping.slice(0, 3).join(", ")}...`);
        }
        if (shipData.service_corrections?.length) {
          toast.warning(`Auto-corrected services: ${shipData.service_corrections.slice(0, 3).join(", ")}${shipData.service_corrections.length > 3 ? '...' : ''}`);
        }
      }
      // Cleanup errors pass
      await fetch(`${BACKEND_URL}/api/shipment-costing/cleanup-errors`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      });

      const totalUpdated = priceUpdated + shipUpdated;
      toast.success(`Recalculated ${totalUpdated} orders (${priceUpdated} price + ${shipUpdated} shipping)`);
      if (finalPriceData.fixed_products?.length) {
        toast.info(`Fixed products: ${finalPriceData.fixed_products.slice(0, 3).join(", ")}...`);
      }
      handleRefresh();
    } finally {
      // Auto-dismiss the progress card ~4s after done so the user sees the final numbers
      setTimeout(() => setRecalcProgress(null), 4000);
    }
  };

  // Recalculate specific tracking numbers (for filtered/search results)
  const handleRecalculateSelected = async (trackingNumbers) => {
    if (!trackingNumbers || trackingNumbers.length === 0) {
      toast.error("No tracking numbers to recalculate");
      return;
    }
    
    try {
      setLoading(true);
      toast.info(`Recalculating ${trackingNumbers.length} order(s)...`);
      
      const response = await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-by-tracking`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ 
          tracking_numbers: trackingNumbers,
          sync_to_sales: true
        })
      });
      
      if (!response.ok) {
        throw new Error("Recalculation failed");
      }
      
      const data = await response.json();
      
      if (data.updated_count > 0) {
        toast.success(`Recalculated ${data.updated_count} orders${data.sales_synced ? `, synced ${data.sales_synced} to sales` : ''}`);
        
        // Show details of what was updated
        if (data.updated_orders && data.updated_orders.length > 0) {
          const sample = data.updated_orders.slice(0, 3);
          for (const order of sample) {
            toast.info(`${order.tracking}: ${order.old_cost} → ${order.new_cost} (${order.matched_product})`);
          }
        }
      } else {
        toast.warning("No orders were updated - check if products exist in database");
      }
      
      // Refresh the orders list
      handleRefresh();
    } catch (error) {
      console.error("Recalculate error:", error);
      toast.error("Failed to recalculate selected orders");
    } finally {
      setLoading(false);
    }
  };

  // Cleanup errors - move Weight Not Found/Price errors to notifications when appropriate
  const handleCleanupErrors = async () => {
    try {
      setLoading(true);
      toast.info("Cleaning up errors...");
      
      const response = await fetch(`${BACKEND_URL}/api/shipment-costing/cleanup-errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error("Cleanup failed");
      }
      
      const result = await response.json();
      toast.success(`Fixed ${result.updated_count} orders (${result.weight_errors_moved} weight, ${result.price_errors_moved} price errors moved to notifications)`);
      
      // Refresh the orders list
      handleRefresh();
    } catch (error) {
      console.error("Cleanup error:", error);
      toast.error("Failed to cleanup errors");
    } finally {
      setLoading(false);
    }
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue("");
  };

  // Handle key press in edit mode
  const handleEditKeyDown = (e, rowIdx, field) => {
    if (e.key === "Enter") {
      handleEditSave(rowIdx, field);
    } else if (e.key === "Escape") {
      handleEditCancel();
    }
  };

  // Load persisted data on component mount
  useEffect(() => {
    const ctrl = new AbortController();
    const fetchPersistedData = async () => {
      try {
        setLoadingData(true);
        const startTime = performance.now(); // Start timing
        // Build query params for server-side pagination
        const params = new URLSearchParams();
        params.set('page', serverPage.toString());
        params.set('page_size', SERVER_PAGE_SIZE.toString());
        
        // Only add search param if we have 3+ characters
        // If search term is 1-2 chars, don't search (wait for more input or clear)
        if (serverSearchTerm && serverSearchTerm.length >= 3) {
          params.set('search', serverSearchTerm);
        }
        if (filterClient) {
          params.set('client', filterClient);
        }
        // Server-side date filtering
        if (filterDateFrom) {
          params.set('date_from', filterDateFrom);
        }
        if (filterDateTo) {
          params.set('date_to', filterDateTo);
        }
        
        const response = await fetch(`${BACKEND_URL}/api/shipment-costing/orders?${params}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: ctrl.signal,
        });
        
        const endTime = performance.now(); // End timing
        setLoadTime(Math.round(endTime - startTime)); // Store load time in ms
        
        if (response.ok) {
          const data = await response.json();
          if (ctrl.signal.aborted) return;
          // Always set orders, even if empty array (search returned no results)
          setOrders(data.orders || []);
          setSummary(data.summary || null);
          setSheetsProcessed(data.sheets_processed || []);
          // Store server pagination info
          if (data.pagination) {
            setServerPagination(data.pagination);
          }
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        console.error("Failed to load persisted data:", error);
        setLoadTime(null);
        // Silent fail - user can upload new data
      } finally {
        if (!ctrl.signal.aborted) setLoadingData(false);
      }
    };

    // Only fetch if search term is empty OR has 3+ characters
    // Don't fetch while user is typing 1-2 characters (intermediate state)
    if (serverSearchTerm.length === 0 || serverSearchTerm.length >= 3) {
      fetchPersistedData();
    }
    // Expose to inline-edit save handler so it can refresh after a save.
    fetchPersistedDataRef.current = fetchPersistedData;
    return () => ctrl.abort();
  }, [token, serverPage, serverSearchTerm, filterClient, filterDateFrom, filterDateTo]);

  // Fetch DP CLIENT codes for filtering
  useEffect(() => {
    const fetchDpClientCodes = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/ledgers`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          const ledgers = data.ledgers || data || [];
          
          // Get ALL client codes from ledgers (for dropdown)
          const allClients = ledgers
            .map(l => (l.client_code || '').toUpperCase())
            .filter(Boolean);
          setAllLedgerClients([...new Set(allClients)].sort());
          
          // Get DP CLIENT codes (filtered by role)
          const dpClients = ledgers
            .filter(l => (l.role || '').toLowerCase() === 'client' || (l.role || '').toLowerCase() === 'dp client')
            .map(l => (l.client_code || '').toUpperCase());
          setDpClientCodes(dpClients);
        }
      } catch (error) {
        console.error("Failed to fetch DP CLIENT codes:", error);
      }
    };
    
    if (token) {
      fetchDpClientCodes();
    }
  }, [token]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  // Filter and search orders
  const applyClientSideFilters = (source) => {
    let result = source;
    
    // Filter by errors only (general toggle)
    if (filterErrors) {
      result = result.filter(o => o.errors && o.errors.length > 0);
    }
    
    // Filter by successful only (no errors)
    if (filterSuccessOnly) {
      result = result.filter(o => !o.errors || o.errors.length === 0);
    }
    
    // Filter by notifications only (general toggle)
    if (filterNotifications) {
      result = result.filter(o => o.notifications && o.notifications.length > 0);
    }
    
    // Filter by specific notification type
    if (filterNotificationType) {
      result = result.filter(o => {
        if (!o.notifications || o.notifications.length === 0) return false;
        // Handle patterns like "Weight exceeds max slab"
        if (filterNotificationType === 'Weight exceeds max slab') {
          return o.notifications.some(n => n.includes('Weight') && n.includes('exceeds'));
        }
        return o.notifications.some(n => n.includes(filterNotificationType));
      });
    }
    
    // Filter by replaced tracking numbers
    if (filterReplacedTracking) {
      result = result.filter(o => o.is_strikethrough === true || o.superseded_by);
    }
    
    // Filter by orders without date
    if (filterWithoutDate) {
      result = result.filter(o => !o.order_date || o.order_date === "" || o.order_date === null);
    }
    
    // Filter by orders with auto-assigned date
    if (filterAutoAssignedDate) {
      result = result.filter(o => o.date_auto_assigned === true);
    }
    
    // Filter by specific error type
    if (filterErrorType) {
      result = result.filter(o => {
        if (!o.errors || o.errors.length === 0) return false;
        // Handle "Product not found" which has dynamic product names
        if (filterErrorType === 'Product not found') {
          return o.errors.some(err => err.startsWith('Product not found'));
        }
        // Handle "Price Missing" - check if any error contains "Price Missing"
        if (filterErrorType === 'Price Missing') {
          return o.errors.some(err => err.includes('Price Missing'));
        }
        // For other error types, use exact match or partial match
        return o.errors.some(err => err === filterErrorType || err.includes(filterErrorType));
      });
    }
    
    // Filter by Service
    if (filterService) {
      result = result.filter(o => (o.detected_service || "").toUpperCase() === filterService.toUpperCase());
    }
    
    // Filter by Country
    if (filterCountry) {
      result = result.filter(o => (o.detected_country || "").toUpperCase() === filterCountry.toUpperCase());
    }
    
    // Filter by DP CLIENTS Only - show only orders from DP CLIENT ledgers
    if (filterDpClientsOnly && dpClientCodes.length > 0) {
      result = result.filter(o => {
        const clientCode = (o.client_code || "").toUpperCase();
        return dpClientCodes.includes(clientCode);
      });
    }
    
    // Filter by Client - include ALL rows (products) for matching tracking numbers
    if (filterClient) {
      // First, find all tracking numbers that belong to this client
      const clientTrackings = new Set(
        result
          .filter(o => (o.client_code || "").toUpperCase() === filterClient.toUpperCase())
          .map(o => o.tracking?.toUpperCase())
          .filter(Boolean)
      );
      // Then include all rows (products) with those tracking numbers
      result = result.filter(o => {
        const tracking = (o.tracking || "").toUpperCase();
        return clientTrackings.has(tracking);
      });
    }
    
    // Filter by Minimum Cost
    if (filterCostMin && !isNaN(parseFloat(filterCostMin))) {
      const minCost = parseFloat(filterCostMin);
      result = result.filter(o => (o.total_cost || 0) >= minCost);
    }
    
    // NOTE: Date filtering is now handled SERVER-SIDE in the API call.
    // The server filters by date_from/date_to before pagination.
    
    // Search - include ALL rows (products) for matching tracking numbers
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      
      // First pass: find all matching rows
      const matchingRows = result.filter(o => 
        (o.client_code || "").toLowerCase().includes(term) ||
        (o.tracking || "").toLowerCase().includes(term) ||
        (o.product || "").toLowerCase().includes(term) ||
        (o.second_name || "").toLowerCase().includes(term) ||
        (o.detected_service || "").toLowerCase().includes(term) ||
        (o.detected_country || "").toLowerCase().includes(term) ||
        // Search by order number (both order_no and order_number fields)
        String(o.order_no || "").toLowerCase().includes(term) ||
        String(o.order_number || "").toLowerCase().includes(term)
      );
      
      // Collect all tracking numbers from matching rows
      const matchingTrackings = new Set(
        matchingRows.map(o => o.tracking?.toUpperCase()).filter(Boolean)
      );
      
      // If searching by client code or tracking, include all products in those parcels
      if (matchingTrackings.size > 0) {
        result = result.filter(o => {
          const tracking = (o.tracking || "").toUpperCase();
          return matchingTrackings.has(tracking);
        });
      } else {
        // No tracking matches, use direct filter
        result = matchingRows;
      }
    }
    
    // Sort by date ascending when date filter is active
    if (filterDateFrom || filterDateTo) {
      result = result.sort((a, b) => {
        const dateA = a.order_date || "";
        const dateB = b.order_date || "";
        
        // Convert DDMMYY to comparable number (YYMMDD)
        const convertToComparable = (ddmmyy) => {
          if (!ddmmyy || ddmmyy.length !== 6) return 0;
          const dd = ddmmyy.substring(0, 2);
          const mm = ddmmyy.substring(2, 4);
          const yy = ddmmyy.substring(4, 6);
          return parseInt(yy + mm + dd);
        };
        
        return convertToComparable(dateA) - convertToComparable(dateB);
      });
    }
    
    // When filtering by client or searching, sort by DATE first, then tracking to keep parcel rows together
    if (filterClient || searchTerm) {
      const convertToComparable = (ddmmyy) => {
        if (!ddmmyy || ddmmyy.length !== 6) return 0;
        const dd = ddmmyy.substring(0, 2);
        const mm = ddmmyy.substring(2, 4);
        const yy = ddmmyy.substring(4, 6);
        return parseInt(yy + mm + dd);
      };
      // Build a map of tracking → date from first rows, so all rows in a parcel share the same sort date
      const trackingDateMap = {};
      result.forEach(o => {
        const t = (o.tracking || "").toUpperCase();
        if (t && o.is_order_first_row && o.order_date) {
          trackingDateMap[t] = convertToComparable(o.order_date);
        }
      });
      result = result.sort((a, b) => {
        const tA = (a.tracking || "").toUpperCase();
        const tB = (b.tracking || "").toUpperCase();
        // Use parcel group date (from first row) for sorting
        const dateA = trackingDateMap[tA] || convertToComparable(a.order_date || "");
        const dateB = trackingDateMap[tB] || convertToComparable(b.order_date || "");
        if (dateA !== dateB) return dateA - dateB;
        // Same date: sort by tracking to keep parcel rows together
        if (tA !== tB) return tA.localeCompare(tB);
        // Same tracking: first row comes first, then products
        if (a.is_order_first_row && !b.is_order_first_row) return -1;
        if (!a.is_order_first_row && b.is_order_first_row) return 1;
        const productA = (a.product || a.second_name || "").toLowerCase();
        const productB = (b.product || b.second_name || "").toLowerCase();
        return productA.localeCompare(productB);
      });
    }
    
    return result;
  };

  const filteredOrders = useMemo(
    () => applyClientSideFilters(orders),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orders, filterErrors, filterSuccessOnly, filterNotifications, filterNotificationType, filterReplacedTracking, filterWithoutDate, filterAutoAssignedDate, filterErrorType, filterService, filterCountry, filterClient, filterCostMin, filterDpClientsOnly, dpClientCodes, searchTerm]
  );

  // Fetch EVERY row matching the current server-side filters (client / search /
  // date range) by paging through the API — exports must contain the FULL
  // dataset, not just the 500 rows loaded on screen.
  const fetchAllFilteredOrders = async () => {
    const all = [];
    const EXPORT_PAGE_SIZE = 5000;
    let page = 1;
    for (;;) {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('page_size', String(EXPORT_PAGE_SIZE));
      params.set('skip_summary', 'true');
      if (serverSearchTerm && serverSearchTerm.length >= 3) params.set('search', serverSearchTerm);
      if (filterClient) params.set('client', filterClient);
      if (filterDateFrom) params.set('date_from', filterDateFrom);
      if (filterDateTo) params.set('date_to', filterDateTo);
      const resp = await fetch(`${BACKEND_URL}/api/shipment-costing/orders?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error(`Export fetch failed (page ${page})`);
      const data = await resp.json();
      all.push(...(data.orders || []));
      const pg = data.pagination;
      if (!pg || !pg.has_more || page >= 200) break;
      page += 1;
      toast.info(`Preparing export… ${all.length.toLocaleString()} rows loaded`, { id: 'export-progress' });
    }
    return all;
  };

  // Full filtered dataset for exports/prints: fetch everything from the server,
  // then apply the same client-side filters as the on-screen table. Falls back
  // to the currently loaded rows if the fetch fails.
  const getExportData = async () => {
    try {
      toast.info('Preparing export — fetching all matching rows…', { id: 'export-progress' });
      const allRows = await fetchAllFilteredOrders();
      toast.dismiss('export-progress');
      return applyClientSideFilters(allRows);
    } catch (e) {
      console.error('Full export fetch failed, falling back to loaded rows:', e);
      toast.dismiss('export-progress');
      toast.warning('Could not fetch full dataset — exporting currently loaded rows only');
      return hasActiveFilters ? filteredOrders : orders;
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);

  // Date range for filtered orders
  const filteredDateRange = useMemo(() => {
    const parseDDMMYY = (d) => {
      if (!d || d.length < 6) return null;
      const dd = d.substring(0,2), mm = d.substring(2,4), yy = d.substring(4,6);
      const date = new Date(2000 + parseInt(yy), parseInt(mm) - 1, parseInt(dd));
      return isNaN(date.getTime()) ? null : date;
    };
    const valid = filteredOrders
      .filter(o => o.order_date && o.order_date.length >= 6)
      .map(o => ({ raw: o.order_date, parsed: parseDDMMYY(o.order_date) }))
      .filter(d => d.parsed);
    if (valid.length === 0) return null;
    valid.sort((a, b) => a.parsed - b.parsed);
    return { from: valid[0].raw, to: valid[valid.length - 1].raw };
  }, [filteredOrders]);
  const paginatedOrders = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredOrders.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredOrders, currentPage]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setServerPage(1);
  }, [searchTerm, filterErrors, filterSuccessOnly, filterReplacedTracking, filterWithoutDate, filterAutoAssignedDate, filterService, filterCountry, filterClient, filterCostMin, filterDateFrom, filterDateTo, filterDpClientsOnly]);
  
  // Clear all filters
  const clearFilters = () => {
    setFilterErrors(false);
    setFilterSuccessOnly(false);
    setFilterReplacedTracking(false);
    setFilterWithoutDate(false);
    setFilterAutoAssignedDate(false);
    setFilterErrorType("");
    setFilterService("");
    setFilterCountry("");
    setFilterClient("");
    setFilterCostMin("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterDpClientsOnly(false);
    setSearchTerm("");
    setServerSearchTerm("");
    setPendingSearchTerm("");
    setServerPage(1);
  };
  
  // Check if any filter is active
  const hasActiveFilters = filterErrors || filterSuccessOnly || filterNotifications || filterNotificationType || filterReplacedTracking || filterWithoutDate || filterAutoAssignedDate || filterErrorType || filterService || filterCountry || filterClient || filterCostMin || filterDateFrom || filterDateTo || filterDpClientsOnly || searchTerm || serverSearchTerm;

  // Handle file upload
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.xlsb')) {
      toast.error("Please upload an Excel file (.xlsx, .xls, or .xlsb)");
      return;
    }

    setUploading(true);
    
    // Check file size - skip date conflict check for large files to avoid timeout
    const fileSizeMB = file.size / (1024 * 1024);
    
    // For large files (>5MB), skip date conflict check and go directly to async processing
    if (fileSizeMB > 5) {
      toast.info(`Large file (${fileSizeMB.toFixed(1)}MB) - processing in background...`);
      // Read file data once
      let fileData;
      try {
        const blob = file.slice(0, file.size, file.type);
        fileData = await blob.arrayBuffer();
      } catch (readError) {
        console.error("File read error:", readError);
        toast.error("Error reading file: " + (readError.message || "Unknown error"));
        setUploading(false);
        e.target.value = '';
        return;
      }
      
      // Create fresh file and process directly with async endpoint
      const freshFile = new File([fileData], file.name, { 
        type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      await processUpload(freshFile, fileData);
      e.target.value = '';
      return;
    }
    
    // For smaller files, do the normal flow with date conflict check
    let fileData;
    try {
      // Use slice() to create a copy - more reliable across browsers
      const blob = file.slice(0, file.size, file.type);
      fileData = await blob.arrayBuffer();
    } catch (readError) {
      console.error("File read error:", readError);
      toast.error("Error reading file: " + (readError.message || "Unknown error"));
      setUploading(false);
      e.target.value = '';
      return;
    }

    // Create a helper function to get a fresh Blob/File from the stored data
    const createFreshFile = () => {
      const blob = new Blob([fileData], { 
        type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      return new File([blob], file.name, { 
        type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
    };

    // Check for date conflicts (only for small files)
    try {
      const checkFormData = new FormData();
      checkFormData.append("file", createFreshFile());

      const checkResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/check-date-conflict`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: checkFormData
      });

      if (!checkResponse.ok) {
        const errorText = await checkResponse.text();
        let errorMsg = "Error checking file";
        try {
          const errorJson = JSON.parse(errorText);
          errorMsg = errorJson.detail || errorMsg;
        } catch {
          errorMsg = errorText || errorMsg;
        }
        throw new Error(errorMsg);
      }

      const checkData = await checkResponse.json();

      if (checkData.has_conflict) {
        // There are date conflicts - show dialog
        // Store the file data for later use after conflict resolution
        setDateConflictData(checkData);
        setPendingUploadFile({ data: fileData, name: file.name, type: file.type, size: file.size });
        setShowDateConflictDialog(true);
        setUploading(false);
        // Reset file input
        e.target.value = '';
        return;
      }

      // No conflicts - proceed with normal upload using fresh file
      await processUpload(createFreshFile(), fileData);
      
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error checking file: " + (error.message || "Unknown error"));
      setUploading(false);
    }
    
    // Reset file input
    e.target.value = '';
  };

  // Process the actual upload
  // Poll background task status
  const pollBackgroundTask = async (taskId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/background-tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error("Failed to check task status");
      }
      
      const taskData = await response.json();
      
      setUploadProgress(taskData.progress || 0);
      setUploadProgressMessage(taskData.progress_message || "Processing...");
      
      if (taskData.status === "completed") {
        // Task completed - process results
        setBackgroundTaskId(null);
        setUploading(false);
        
        const data = taskData.result;
        if (data && data.orders) {
          // Backend auto-saved orders — just refresh the view
          if (data.auto_saved) {
            setShowUploadDialog(false);
            setUploadSummaryResult(data);
            handleRefresh();
          } else {
            // Fallback: old behavior — save from frontend (for backward compatibility)
            const newOrders = data.orders || [];
            const combinedOrders = [...orders, ...newOrders];
            
            setOrders(combinedOrders);
            setSheetsProcessed(prev => {
              const newSheets = data.sheets_processed || [];
              return [...new Set([...prev, ...newSheets])];
            });
            
            const totalMedicine = combinedOrders.reduce((sum, r) => sum + (r.medicine_cost || 0), 0);
            const totalShipping = combinedOrders.filter(r => r.is_order_first_row !== false).reduce((sum, r) => sum + (r.shipping_cost || 0), 0);
            const totalCost = combinedOrders.filter(r => r.is_order_first_row !== false).reduce((sum, r) => sum + (r.total_cost || 0), 0);
            const errorCount = combinedOrders.filter(r => r.errors && r.errors.length > 0).length;
            const uniqueKeys = new Set(combinedOrders.map(r => r.order_group_key || `row_${r.row_number}`));
            
            setSummary({
              total_orders: uniqueKeys.size, total_rows: combinedOrders.length,
              total_products: combinedOrders.length, total_instructions: 0,
              total_medicine_cost: Math.round(totalMedicine * 100) / 100,
              total_shipping_cost: Math.round(totalShipping * 100) / 100,
              total_cost: Math.round(totalCost * 100) / 100,
              orders_with_errors: errorCount
            });
            
            setShowUploadDialog(false);
            toast.success(data.message || "Upload successful");
            
            try {
              const checkDupResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/check-duplicates`, {
                method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({ orders: newOrders })
              });
              if (checkDupResponse.ok) {
                const dupData = await checkDupResponse.json();
                if (dupData.has_duplicates) { setDuplicateData(dupData); setPendingNewOrders(newOrders); setShowDuplicateDialog(true); return; }
              }
            } catch (checkErr) { console.error("Error checking duplicates:", checkErr); }
            
            await saveOrdersToDatabase(newOrders);
          }
        }
        return; // Stop polling
      } else if (taskData.status === "failed") {
        // Task failed
        setBackgroundTaskId(null);
        setUploading(false);
        toast.error(taskData.error || "Processing failed");
        return; // Stop polling
      }
      
      // Still processing - continue polling
      setTimeout(() => pollBackgroundTask(taskId), 3000);
      
    } catch (error) {
      console.error("Error polling task:", error);
      setBackgroundTaskId(null);
      setUploading(false);
      toast.error("Error checking upload status");
    }
  };

  const processUpload = async (file, fileData = null) => {
    setUploading(true);
    setUploadProgress(0);
    setUploadProgressMessage("Uploading file...");
    
    // If file is a stored object with data, create a fresh File from it
    let fileToUpload = file;
    if (file.data && !file.arrayBuffer) {
      // This is a stored file object from conflict dialog
      fileToUpload = new File([file.data], file.name, { 
        type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
    }
    
    const formData = new FormData();
    formData.append("file", fileToUpload);
    
    // Use async processing for files > 6MB to avoid timeout
    const fileSizeMB = fileToUpload.size / (1024 * 1024);
    const useAsync = fileSizeMB > 6;

    try {
      if (useAsync) {
        // ==================== ASYNC PROCESSING FOR LARGE FILES ====================
        setUploadProgressMessage("Uploading large file...");
        
        const response = await fetch(`${BACKEND_URL}/api/shipment-costing/process-async`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });
        
        // Read response body once
        let data;
        try {
          data = await response.json();
        } catch (jsonError) {
          console.error("Failed to parse response:", jsonError);
          data = null;
        }
        
        if (response.ok && data) {
          setBackgroundTaskId(data.task_id);
          setUploadProgressMessage("Processing started. Please wait...");
          toast.info("Large file uploaded. Processing in background...");
          
          // Start polling for status
          setTimeout(() => pollBackgroundTask(data.task_id), 2000);
        } else {
          setUploading(false);
          toast.error(data?.detail || "Upload failed");
        }
      } else {
        // ==================== SYNC PROCESSING FOR SMALL FILES ====================
        const response = await fetch(`${BACKEND_URL}/api/shipment-costing/process`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });

        // Read response body once
        let data;
        try {
          data = await response.json();
        } catch {
          data = null;
        }

        if (response.ok && data) {
          // APPEND mode: combine new orders with existing orders
          const newOrders = data.orders || [];
          const combinedOrders = [...orders, ...newOrders];
          
          setOrders(combinedOrders);
          setSheetsProcessed(prev => {
            const newSheets = data.sheets_processed || [];
            return [...new Set([...prev, ...newSheets])];
          });
          
          // Recalculate summary using ORDER-LEVEL totals (parcel-level calculations)
          // Medicine cost: sum of all rows (row-level)
          const totalMedicine = combinedOrders.reduce((sum, r) => sum + (r.medicine_cost || 0), 0);
          
          // Shipping cost: ONLY from first rows of each order to avoid double counting
          const totalShipping = combinedOrders
            .filter(r => r.is_order_first_row !== false) // Include rows where is_order_first_row is true or undefined
            .reduce((sum, r) => sum + (r.shipping_cost || 0), 0);
          
          // Total cost: ONLY from first rows of each order
          const totalCost = combinedOrders
            .filter(r => r.is_order_first_row !== false)
            .reduce((sum, r) => sum + (r.total_cost || 0), 0);
          
          const errorCount = combinedOrders.filter(r => r.errors && r.errors.length > 0).length;
          
          // Count unique orders by order_group_key
          const uniqueKeys = new Set(combinedOrders.map(r => r.order_group_key || `row_${r.row_number}`));
          const uniqueOrderCount = uniqueKeys.size;
          
          setSummary({
            total_orders: uniqueOrderCount,
            total_rows: combinedOrders.length,
            total_products: combinedOrders.length,
            total_instructions: 0,
            total_medicine_cost: Math.round(totalMedicine * 100) / 100,
            total_shipping_cost: Math.round(totalShipping * 100) / 100,
            total_cost: Math.round(totalCost * 100) / 100,
            orders_with_errors: errorCount
          });
          
          setShowUploadDialog(false);
          toast.success(data.message || "Upload successful");
          
          // Check for duplicate tracking numbers before saving
          try {
            const checkDupResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/check-duplicates`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({ orders: newOrders })
            });
            
            if (checkDupResponse.ok) {
              const dupData = await checkDupResponse.json();
              
              if (dupData.has_duplicates) {
                // Show duplicate warning dialog
                setDuplicateData(dupData);
                setPendingNewOrders(newOrders);
                setShowDuplicateDialog(true);
                setUploading(false);
                return; // Don't save yet - wait for user decision
              }
            }
          } catch (checkErr) {
            console.error("Error checking duplicates:", checkErr);
            // Continue with save even if check fails
          }
          
          // No duplicates found - save directly
          await saveOrdersToDatabase(newOrders);
          setUploading(false);
        } else {
          toast.error((data && data.detail) || "Upload failed");
          setUploading(false);
        }
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Upload failed: " + error.message);
      setUploading(false);
    }
  };

  // Handle date conflict - user chooses to add new data only
  const handleAddNewOnly = async () => {
    setShowDateConflictDialog(false);
    if (pendingUploadFile) {
      await processUpload(pendingUploadFile);
    }
    setPendingUploadFile(null);
    setDateConflictData(null);
  };

  // Helper function to save orders to database
  const saveOrdersToDatabase = async (ordersToSave, saveMode = "append") => {
    try {
      const saveResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          orders: ordersToSave,
          save_mode: saveMode
        })
      });
      
      if (saveResponse.ok) {
        const saveData = await saveResponse.json();
        console.log("Save response:", saveData);
        
        let message = `${saveData.orders_saved} orders saved`;
        if (saveData.orders_replaced > 0) {
          message += ` (replaced ${saveData.orders_replaced} existing)`;
        }
        if (saveData.orders_skipped > 0) {
          message += ` (skipped ${saveData.orders_skipped} duplicates)`;
        }
        toast.success(message);
        return saveData;
      } else {
        const errorData = await saveResponse.json().catch(() => ({}));
        console.error("Save failed:", errorData);
        toast.error(`Failed to save to database: ${errorData.detail || 'Unknown error'}`);
        return null;
      }
    } catch (saveError) {
      console.error("Failed to save data:", saveError);
      toast.error("Failed to save data to database - check console for details");
      return null;
    }
  };

  // Handle duplicate tracking - user chooses to skip duplicates
  const handleSkipDuplicates = async () => {
    setShowDuplicateDialog(false);
    if (pendingNewOrders.length > 0) {
      // Use backend to skip duplicates
      await saveOrdersToDatabase(pendingNewOrders, "skip_duplicates");
    }
    setPendingNewOrders([]);
    setDuplicateData(null);
  };

  // Handle duplicate tracking - user chooses to save anyway (REPLACE existing)
  const handleSaveWithDuplicates = async () => {
    setShowDuplicateDialog(false);
    if (pendingNewOrders.length > 0) {
      // Use backend to REPLACE existing orders with same tracking
      await saveOrdersToDatabase(pendingNewOrders, "replace");
    }
    setPendingNewOrders([]);
    setDuplicateData(null);
  };

  // Handle duplicate tracking - user cancels
  const handleCancelDuplicates = () => {
    setShowDuplicateDialog(false);
    setPendingNewOrders([]);
    setDuplicateData(null);
    toast.info("Upload cancelled - no data saved");
  };

  // Auto-generate DP Client Sales
  const handleAutoGenerateDPSales = async (batchId = null) => {
    setGeneratingDPSales(true);
    setDpSalesResult(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/sales/auto-generate-dp-client`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          batch_id: batchId
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setDpSalesResult(data);
        setShowDPSalesDialog(true);
        
        if (data.generated > 0) {
          toast.success(`Generated ${data.generated} sales records for DP Clients`);
        } else if (data.error_count > 0) {
          toast.warning(`No sales generated - ${data.error_count} errors (missing prices)`);
        } else {
          toast.info("No DP Client orders found to process");
        }
      } else {
        toast.error(data.detail || "Failed to generate DP Client sales");
      }
    } catch (error) {
      console.error("DP Client auto-generation error:", error);
      toast.error("Failed to generate DP Client sales");
    } finally {
      setGeneratingDPSales(false);
    }
  };

  // Handle date conflict - user chooses to overwrite
  const handleOverwriteRequest = () => {
    setShowDateConflictDialog(false);
    setShowPasswordDialog(true);
  };

  // Validate password and perform overwrite
  const handlePasswordSubmit = async () => {
    if (!overwritePassword) {
      toast.error("Please enter the password");
      return;
    }

    try {
      // Delete existing orders for conflicting dates
      const deleteResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/delete-by-dates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          dates: dateConflictData.conflicting_dates,
          password: overwritePassword
        })
      });

      // Handle response - avoid reading body twice
      if (!deleteResponse.ok) {
        if (deleteResponse.status === 403) {
          toast.error("Invalid password! Password should be today's date in DDMM format");
        } else {
          try {
            const errorData = await deleteResponse.json();
            toast.error(errorData.detail || "Failed to delete existing orders");
          } catch {
            toast.error("Failed to delete existing orders");
          }
        }
        return;
      }

      // Only read JSON if response was ok
      let deleteData;
      try {
        deleteData = await deleteResponse.json();
      } catch {
        deleteData = { deleted_count: 0 };
      }

      toast.success(`Deleted ${deleteData.deleted_count || 0} existing orders`);

      // Reload existing orders (without deleted ones)
      await handleRefresh();

      // Now process the upload
      if (pendingUploadFile) {
        await processUpload(pendingUploadFile);
      }

      setShowPasswordDialog(false);
      setOverwritePassword("");
      setPendingUploadFile(null);
      setDateConflictData(null);
      
    } catch (error) {
      console.error("Overwrite error:", error);
      toast.error("Failed to overwrite: " + (error.message || "Unknown error"));
    }
  };

  // Handle Packing List Upload — parsed SERVER-SIDE (same canonical parser as
  // Upload Center) so every tab is read reliably regardless of browser/memory.
  const handlePackingListUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls') && !file.name.endsWith('.xlsb')) {
      toast.error("Please upload an Excel file (.xlsx, .xls, or .xlsb)");
      e.target.value = '';
      return;
    }

    setUploadingPacking(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${BACKEND_URL}/api/shipment-costing/upload-packing-async`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const d = await res.json();
      if (!res.ok || !d.task_id) {
        toast.error(d.detail || "Failed to upload packing list");
        return;
      }
      toast.loading("Parsing packing sheet (all tabs)...", { id: "packing-parse" });

      const started = Date.now();
      while (Date.now() - started < 10 * 60 * 1000) {
        await new Promise((r) => setTimeout(r, 2000));
        let td = null;
        try {
          const tr = await fetch(`${BACKEND_URL}/api/background-tasks/${d.task_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          td = await tr.json();
        } catch {
          continue; // transient poll error
        }
        if (td && td.status === 'completed') {
          const r = td.result || {};
          const sr = await fetch(`${BACKEND_URL}/api/shipment-costing/get-packing-list`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const sd = await sr.json();
          if (sd.has_packing_list && sd.packing_data && Object.keys(sd.packing_data).length > 0) {
            setPackingListData(sd.packing_data);
            setPackingListFile(file.name);
            const msg = r.message || `Packing list loaded: ${Object.keys(sd.packing_data).length} parcels`;
            if ((r.failed_sheets || []).length > 0) {
              toast.warning(msg, { id: "packing-parse", duration: 12000 });
            } else {
              toast.success(msg, { id: "packing-parse", duration: 8000 });
            }
            setShowWeightSourceDialog(true);
          } else {
            toast.error(r.message || "No valid packing data found in file", { id: "packing-parse" });
          }
          return;
        }
        if (td && td.status === 'failed') {
          toast.error("Packing parse failed: " + (td.error || "unknown error"), { id: "packing-parse" });
          return;
        }
        if (td && td.progress_message) {
          toast.loading(td.progress_message, { id: "packing-parse" });
        }
      }
      toast.warning("Parsing is taking unusually long — please try again shortly", { id: "packing-parse" });
    } catch (error) {
      toast.error("Failed to upload packing list: " + error.message, { id: "packing-parse" });
      console.error(error);
    } finally {
      setUploadingPacking(false);
      e.target.value = '';
    }
  };

  // Recalculate with packing list data — runs as a BACKGROUND job with live
  // progress so large sheets never hit HTTP timeouts.
  const handleRecalculateWithPackingList = async () => {
    if (!packingListData || Object.keys(packingListData).length === 0) {
      toast.error("No packing list data loaded");
      return;
    }

    setLoading(true);
    try {
      const jobId = (crypto.randomUUID && crypto.randomUUID()) || `pk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const response = await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-with-packing`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ packing_data: packingListData, weight_source: weightSource, job_id: jobId })
      });
      const data = await response.json();
      if (!response.ok || data.status !== "started") {
        toast.error(data.detail || "Failed to start recalculation");
        return;
      }
      setShowWeightSourceDialog(false);
      toast.loading("Recalculating parcels in background...", { id: "packing-recalc" });

      const started = Date.now();
      const poll = async () => {
        if (Date.now() - started > 20 * 60 * 1000) {
          toast.warning("Recalculation is taking unusually long — check back in a few minutes.", { id: "packing-recalc" });
          return;
        }
        try {
          const pr = await fetch(`${BACKEND_URL}/api/shipment-costing/recalculate-all/progress/${jobId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const pd = await pr.json();
          if (pd && pd.done) {
            if (pd.phase === "error") {
              toast.error(`Recalculation failed: ${pd.error || "unknown error"}`, { id: "packing-recalc" });
            } else {
              let msg = pd.message || "Packing data applied";
              if (pd.weight_updates > 0) msg += ` | Weight updated: ${pd.weight_updates}`;
              if (pd.product_updates > 0) msg += ` | Products synced: ${pd.product_updates}`;
              toast.success(msg, { id: "packing-recalc", duration: 10000 });
              if (pd.unmatched_count > 0) {
                toast.warning(`${pd.unmatched_count} packing parcels had no matching orders (upload the Order Sheet for those dates first)`, { duration: 10000 });
              }
              fetch(`${BACKEND_URL}/api/shipment-costing/cleanup-errors`, {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` }
              }).catch(() => {});
              handleRefresh();
            }
            return;
          }
          if (pd && pd.total > 0) {
            toast.loading(`Recalculating... ${pd.processed || 0}/${pd.total} parcels`, { id: "packing-recalc" });
          }
        } catch {
          // transient poll error — keep polling
        }
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2500);
    } catch (error) {
      toast.error("Failed to recalculate: " + error.message);
      console.error(error);
    } finally {
      setLoading(false);
      setShowWeightSourceDialog(false);
    }
  };

  // Sync missing products from packing list
  const handleSyncPackingProducts = async () => {
    if (!packingListData || Object.keys(packingListData).length === 0) {
      toast.error("No packing list data loaded");
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipment-costing/sync-packing-products`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          packing_data: packingListData
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        if (data.added_count > 0) {
          toast.success(data.message);
          if (data.synced_products && data.synced_products.length > 0) {
            toast.info(`Added: ${data.synced_products.slice(0, 3).join(", ")}${data.synced_products.length > 3 ? '...' : ''}`);
          }
          // Now recalculate with packing list
          await handleRecalculateWithPackingList();
        } else {
          toast.info("No missing products found - all products already exist");
          // Still do the weight recalculation
          await handleRecalculateWithPackingList();
        }
      } else {
        toast.error(data.detail || "Sync failed");
      }
    } catch (error) {
      toast.error("Failed to sync products: " + error.message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Export to Excel
  const handleExportExcel = async () => {
    // Fetch the FULL filtered dataset (all pages) so the export contains
    // every matching row, not just the 500 loaded on screen
    const dataToExport = await getExportData();
    
    if (dataToExport.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      
      // Build filter description for title
      let filterDesc = [];
      if (hasActiveFilters) {
        if (filterErrors) filterDesc.push("Errors Only");
        if (filterSuccessOnly) filterDesc.push("Success Only");
        if (filterErrorType) filterDesc.push(`Error: ${filterErrorType}`);
        if (filterService) filterDesc.push(`Service: ${filterService}`);
        if (filterCountry) filterDesc.push(`Country: ${filterCountry}`);
        if (filterClient) filterDesc.push(`Client: ${filterClient}`);
        if (filterCostMin) filterDesc.push(`Cost ≥ ₹${filterCostMin}`);
        if (filterDateFrom) filterDesc.push(`From: ${filterDateFrom}`);
        if (filterDateTo) filterDesc.push(`To: ${filterDateTo}`);
        if (searchTerm) filterDesc.push(`Search: ${searchTerm}`);
      }
      const filterText = filterDesc.length > 0 ? `Filters: ${filterDesc.join(" | ")}` : "All Orders";
      
      // Prepare data
      const wsData = [
        [`${printSettings.companyName} - Order Cost Sheet`, "", "", "", "", "", "", "", "", "", "", "", "", "", `Generated: ${formatDMY(new Date())}`],
        [filterText],
        [],
        ["Sheet", "Row#", "Client Code", "Date", "Parcel DT", "Order No", "Address", "Product", "Qty", "Tracking", "2nd Name", "Qty (2nd)", "Service", "Country", "Medicine Cost", "Weight (g)", "Shipping Cost", "Total Cost", "Errors"]
      ];

      dataToExport.forEach(o => {
        wsData.push([
          o.sheet_name || "",
          o.row_number || "",
          o.client_code || "",
          o.order_date || "",
          o.parcel_dt || "",
          o.order_no || "",
          (o.address || "").substring(0, 50),
          o.product || "",
          o.quantity || "",
          o.tracking || "",
          o.second_name || "",
          o.qty || "",
          o.detected_service || "",
          o.detected_country || "",
          o.medicine_cost || 0,
          o.product_weight || 0,
          o.shipping_cost || 0,
          o.total_cost || 0,
          (o.errors || []).join(", ")
        ]);
      });

      // Add totals
      wsData.push([]);
      wsData.push([
        "", "", "", "", "", "", "", "", "", "", "", "", "", "TOTAL:",
        dataToExport.reduce((sum, o) => sum + (o.medicine_cost || 0), 0),
        dataToExport.reduce((sum, o) => sum + (o.product_weight || 0), 0),
        dataToExport.reduce((sum, o) => sum + (o.shipping_cost || 0), 0),
        dataToExport.reduce((sum, o) => sum + (o.total_cost || 0), 0),
        ""
      ]);

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Set column widths
      ws["!cols"] = [
        { wch: 12 }, { wch: 6 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 10 },
        { wch: 30 }, { wch: 20 }, { wch: 8 }, { wch: 15 }, { wch: 20 },
        { wch: 8 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 10 },
        { wch: 12 }, { wch: 12 }, { wch: 25 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Order Costs");
      
      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const filename = hasActiveFilters 
        ? `order_cost_filtered_${new Date().toISOString().slice(0,10)}.xlsx`
        : `order_cost_sheet_${new Date().toISOString().slice(0,10)}.xlsx`;
      saveAs(blob, filename);
      
      toast.success(`Excel exported: ${dataToExport.length} orders${hasActiveFilters ? ' (filtered)' : ''}`);
    } catch (error) {
      toast.error("Export failed");
      console.error(error);
    }
  };

  // Export to Google Sheets (CSV)
  const handleExportGoogleSheets = async () => {
    // Fetch the FULL filtered dataset (all pages)
    const dataToExport = await getExportData();
    
    if (dataToExport.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      // Build filter description
      let filterDesc = [];
      if (hasActiveFilters) {
        if (filterErrors) filterDesc.push("Errors Only");
        if (filterSuccessOnly) filterDesc.push("Success Only");
        if (filterErrorType) filterDesc.push(`Error: ${filterErrorType}`);
        if (filterService) filterDesc.push(`Service: ${filterService}`);
        if (filterCountry) filterDesc.push(`Country: ${filterCountry}`);
        if (filterClient) filterDesc.push(`Client: ${filterClient}`);
        if (filterCostMin) filterDesc.push(`Cost >= Rs${filterCostMin}`);
        if (filterDateFrom) filterDesc.push(`From: ${filterDateFrom}`);
        if (filterDateTo) filterDesc.push(`To: ${filterDateTo}`);
        if (searchTerm) filterDesc.push(`Search: ${searchTerm}`);
      }
      const filterText = filterDesc.length > 0 ? `Filters: ${filterDesc.join(" | ")}` : "All Orders";
      
      const csvRows = [
        `${printSettings.companyName} - Order Cost Sheet,,,,,,,,,,,,,,Generated: ${formatDMY(new Date())}`,
        filterText,
        "",
        "Sheet,Row#,Client Code,Date,Parcel DT,Order No,Address,Product,Qty,Tracking,2nd Name,Qty (2nd),Service,Country,Medicine Cost,Weight (g),Shipping Cost,Total Cost,Errors"
      ];

      dataToExport.forEach(o => {
        const row = [
          o.sheet_name || "",
          o.row_number || "",
          o.client_code || "",
          o.order_date || "",
          o.parcel_dt || "",
          o.order_no || "",
          `"${(o.address || "").substring(0, 50).replace(/"/g, '""')}"`,
          o.product || "",
          o.quantity || "",
          o.tracking || "",
          o.second_name || "",
          o.qty || "",
          o.detected_service || "",
          o.detected_country || "",
          o.medicine_cost || 0,
          o.product_weight || 0,
          o.shipping_cost || 0,
          o.total_cost || 0,
          `"${(o.errors || []).join(", ")}"`
        ].join(",");
        csvRows.push(row);
      });

      // Add totals
      csvRows.push("");
      csvRows.push(`,,,,,,,,,,,,,TOTAL:,${dataToExport.reduce((sum, o) => sum + (o.medicine_cost || 0), 0)},${dataToExport.reduce((sum, o) => sum + (o.product_weight || 0), 0)},${dataToExport.reduce((sum, o) => sum + (o.shipping_cost || 0), 0)},${dataToExport.reduce((sum, o) => sum + (o.total_cost || 0), 0)},`);

      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      const filename = hasActiveFilters 
        ? `order_cost_filtered_${new Date().toISOString().slice(0,10)}.csv`
        : `order_cost_sheet_${new Date().toISOString().slice(0,10)}.csv`;
      saveAs(blob, filename);
      
      window.open("https://docs.google.com/spreadsheets/create", "_blank");
      toast.success(`CSV exported: ${dataToExport.length} orders${hasActiveFilters ? ' (filtered)' : ''}! Import into Google Sheets`);
    } catch (error) {
      toast.error("Export failed");
      console.error(error);
    }
  };

  // Full Download - Simplified export for parcel verification
  // Columns: Client Code, Customer Name, Country, Tracking Number, Product Name, Quantity
  const handleFullDownload = async () => {
    // Fetch the FULL filtered dataset (all pages)
    const dataToExport = await getExportData();
    
    if (dataToExport.length === 0) {
      toast.error("No data to export");
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      
      // Get date range description
      let dateDesc = "";
      if (filterDateFrom && filterDateTo) {
        // Parse DDMMYY format
        const parseDate = (d) => {
          if (!d || d.length !== 6) return d;
          return `${d.slice(0,2)}/${d.slice(2,4)}/20${d.slice(4,6)}`;
        };
        dateDesc = `Period: ${parseDate(filterDateFrom)} to ${parseDate(filterDateTo)}`;
      } else {
        dateDesc = `Export Date: ${formatDMY(new Date())}`;
      }
      
      // Prepare data with simplified columns
      const wsData = [
        [`PARCEL VERIFICATION REPORT - ${printSettings.companyName || 'EAC'}`],
        [dateDesc],
        [`Total Parcels: ${dataToExport.length}`],
        [],
        ["Sr No", "Client Code", "Customer Name", "Country", "Tracking Number", "Product Name", "Quantity"]
      ];

      // Add data rows
      dataToExport.forEach((o, idx) => {
        // Combine product name and second name if exists
        let productName = o.product || "";
        if (o.second_name) {
          productName = `${productName}${productName ? " + " : ""}${o.second_name}`;
        }
        
        // Combine quantities - ONLY use strips (qty field), not pills (quantity field)
        let qtyValue = o.qty || "";
        
        wsData.push([
          idx + 1,
          o.client_code || "",
          o.customer_name || o.address?.split(',')[0]?.trim() || "",
          o.detected_country || "",
          o.tracking || "",
          productName,
          qtyValue
        ]);
      });

      // Add summary at the end
      wsData.push([]);
      wsData.push(["", "", "", "", "", "TOTAL PARCELS:", dataToExport.length]);
      
      // Count by client
      const clientCounts = {};
      dataToExport.forEach(o => {
        const client = o.client_code || "Unknown";
        clientCounts[client] = (clientCounts[client] || 0) + 1;
      });
      
      wsData.push([]);
      wsData.push(["CLIENT SUMMARY"]);
      wsData.push(["Client Code", "Parcel Count"]);
      Object.entries(clientCounts)
        .sort((a, b) => b[1] - a[1])
        .forEach(([client, count]) => {
          wsData.push([client, count]);
        });

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Set column widths
      ws["!cols"] = [
        { wch: 8 },   // Sr No
        { wch: 12 },  // Client Code
        { wch: 35 },  // Customer Name
        { wch: 15 },  // Country
        { wch: 20 },  // Tracking Number
        { wch: 40 },  // Product Name
        { wch: 15 }   // Quantity
      ];

      XLSX.utils.book_append_sheet(wb, ws, "Parcel Verification");
      
      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      
      // Generate filename with date range
      let filename = "parcel_verification";
      if (filterDateFrom) {
        filename += `_${filterDateFrom}`;
      }
      if (filterDateTo && filterDateTo !== filterDateFrom) {
        filename += `_to_${filterDateTo}`;
      }
      filename += `.xlsx`;
      
      saveAs(blob, filename);
      
      toast.success(`Full download: ${dataToExport.length} parcels exported for verification`);
    } catch (error) {
      toast.error("Export failed");
      console.error(error);
    }
  };

  // Fix all tracking replacements in database
  const handleFixTrackingReplacements = async () => {
    if (!window.confirm("This will scan ALL orders and fix tracking replacement issues (zero out costs for old tracking, copy costs to new tracking). Continue?")) {
      return;
    }
    
    setFixingTrackingReplacements(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/shipment-costing/scan-fix-all-tracking-replacements`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = await response.json();
      if (response.ok) {
        toast.success(`Fixed ${data.fixes_applied} tracking replacement issues. ${data.strikethrough_zeroed} strikethrough records zeroed.`);
        // Refresh data by re-fetching with current filters
        const refreshParams = new URLSearchParams();
        refreshParams.set('page', '1');
        refreshParams.set('page_size', SERVER_PAGE_SIZE.toString());
        if (filterDateFrom) refreshParams.set('date_from', filterDateFrom);
        if (filterDateTo) refreshParams.set('date_to', filterDateTo);
        const refreshResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/orders?${refreshParams}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (refreshResponse.ok) {
          const refreshData = await refreshResponse.json();
          setOrders(refreshData.orders || []);
          setSummary(refreshData.summary);
          if (refreshData.pagination) {
            setServerPagination(refreshData.pagination);
          }
        }
      } else {
        toast.error(data.detail || "Failed to fix tracking replacements");
      }
    } catch (error) {
      console.error("Error fixing tracking replacements:", error);
      toast.error("Failed to fix tracking replacements");
    } finally {
      setFixingTrackingReplacements(false);
    }
  };

  // Print view with filters
  const handlePrint = async (useCurrentFilters = false) => {
    // Fetch the FULL filtered dataset (all pages) so the print/popup export
    // contains every matching row, not just the 500 loaded on screen
    let baseRows;
    try {
      toast.info('Preparing print — fetching all matching rows…', { id: 'export-progress' });
      baseRows = await fetchAllFilteredOrders();
      toast.dismiss('export-progress');
    } catch (e) {
      console.error('Full print fetch failed, falling back to loaded rows:', e);
      toast.dismiss('export-progress');
      baseRows = orders;
    }
    // When filters are active, always use filtered data by default
    let dataToPrint;
    
    if (hasActiveFilters || useCurrentFilters) {
      // Apply the same client-side filters as the on-screen table
      dataToPrint = applyClientSideFilters(baseRows);
    } else {
      // No active filters - apply print-specific filters if any
      dataToPrint = baseRows;
      if (printFilters.errorsOnly) {
        dataToPrint = dataToPrint.filter(o => o.errors && o.errors.length > 0);
      }
      if (printFilters.service) {
        dataToPrint = dataToPrint.filter(o => (o.detected_service || "").toUpperCase() === printFilters.service.toUpperCase());
      }
      if (printFilters.country) {
        dataToPrint = dataToPrint.filter(o => (o.detected_country || "").toUpperCase() === printFilters.country.toUpperCase());
      }
      if (printFilters.client) {
        dataToPrint = dataToPrint.filter(o => (o.client_code || "").toUpperCase() === printFilters.client.toUpperCase());
      }
      if (printFilters.costMin && !isNaN(parseFloat(printFilters.costMin))) {
        const minCost = parseFloat(printFilters.costMin);
        dataToPrint = dataToPrint.filter(o => (o.total_cost || 0) >= minCost);
      }
    }
    
    if (dataToPrint.length === 0) {
      toast.error("No data to print with selected filters");
      return;
    }

    // Sort print data by date (DDMMYY → YYMMDD), keeping multi-product parcels together
    const convertDateForSort = (ddmmyy) => {
      if (!ddmmyy || ddmmyy.length !== 6) return 0;
      return parseInt(ddmmyy.substring(4,6) + ddmmyy.substring(2,4) + ddmmyy.substring(0,2));
    };
    // Build tracking → date map from first rows
    const trackingDateMap = {};
    dataToPrint.forEach(o => {
      const t = (o.tracking || "").toUpperCase();
      if (t && o.is_order_first_row && o.order_date) {
        trackingDateMap[t] = convertDateForSort(o.order_date);
      }
    });
    dataToPrint = [...dataToPrint].sort((a, b) => {
      const tA = (a.tracking || "").toUpperCase();
      const tB = (b.tracking || "").toUpperCase();
      const dateA = trackingDateMap[tA] || convertDateForSort(a.order_date || "");
      const dateB = trackingDateMap[tB] || convertDateForSort(b.order_date || "");
      if (dateA !== dateB) return dateA - dateB;
      if (tA !== tB) return tA.localeCompare(tB);
      if (a.is_order_first_row && !b.is_order_first_row) return -1;
      if (!a.is_order_first_row && b.is_order_first_row) return 1;
      return 0;
    });

    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.error("Please allow popups to print");
      return;
    }

    // Build filter description
    let filterDesc = [];
    if (hasActiveFilters || useCurrentFilters) {
      if (filterErrors) filterDesc.push("Errors Only");
      if (filterSuccessOnly) filterDesc.push("Success Only");
      if (filterErrorType) filterDesc.push(`Error: ${filterErrorType}`);
      if (filterService) filterDesc.push(`Service: ${filterService}`);
      if (filterCountry) filterDesc.push(`Country: ${filterCountry}`);
      if (filterClient) filterDesc.push(`Client: ${filterClient}`);
      if (filterCostMin) filterDesc.push(`Cost ≥ ₹${filterCostMin}`);
      if (filterDateFrom) filterDesc.push(`From: ${filterDateFrom}`);
      if (filterDateTo) filterDesc.push(`To: ${filterDateTo}`);
      if (searchTerm) filterDesc.push(`Search: ${searchTerm}`);
    } else {
      if (printFilters.errorsOnly) filterDesc.push("Errors Only");
      if (printFilters.service) filterDesc.push(`Service: ${printFilters.service}`);
      if (printFilters.country) filterDesc.push(`Country: ${printFilters.country}`);
      if (printFilters.client) filterDesc.push(`Client: ${printFilters.client}`);
      if (printFilters.costMin) filterDesc.push(`Cost ≥ ₹${printFilters.costMin}`);
    }
    const filterText = filterDesc.length > 0 ? `Filters: ${filterDesc.join(" | ")}` : "All Orders";

    // Calculate date range from data
    const parseDateDDMMYY = (d) => {
      if (!d || d.length < 6) return null;
      const dd = d.substring(0,2), mm = d.substring(2,4), yy = d.substring(4,6);
      return new Date(2000 + parseInt(yy), parseInt(mm) - 1, parseInt(dd));
    };
    const validDates = dataToPrint
      .map(o => ({ raw: o.order_date, parsed: parseDateDDMMYY(o.order_date) }))
      .filter(d => d.parsed && !isNaN(d.parsed.getTime()));
    validDates.sort((a, b) => a.parsed - b.parsed);
    const dateRangeText = validDates.length > 0
      ? `${validDates[0].raw} to ${validDates[validDates.length - 1].raw}`
      : '';

    const styles = `
        @page { size: landscape; margin: 0.5in; }
        .header { text-align: center; margin-bottom: 20px; }
        .header h1 { color: #4338CA; margin: 0; }
        .filters { text-align: center; font-size: 10px; color: #666; margin-top: 5px; padding: 5px; background: #FEF3C7; border-radius: 4px; }
        .summary { display: flex; justify-content: space-around; margin-bottom: 20px; padding: 10px; background: #F1F5F9; border-radius: 8px; }
        .summary-item { text-align: center; }
        .summary-item .value { font-size: 16px; font-weight: bold; color: #4338CA; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #4338CA; color: white; padding: 6px 4px; text-align: left; font-size: 8px; }
        td { border: 1px solid #E2E8F0; padding: 4px; font-size: 8px; }
        tr:nth-child(even) { background: #F8FAFC; }
        .error-row { background: #FEE2E2 !important; }
        .totals { font-weight: bold; background: #E0E7FF !important; }
        .error-text { color: #DC2626; font-size: 7px; }
    `;

    let tableRows = dataToPrint.map((o, idx) => {
      const cols = [];
      cols.push(`<td>${idx + 1}</td>`);
      if (printColumns.client) cols.push(`<td>${o.client_code || '-'}</td>`);
      if (printColumns.date) cols.push(`<td>${formatDMY(o.order_date)}</td>`);
      if (printColumns.service) cols.push(`<td>${o.detected_service || '-'}</td>`);
      if (printColumns.tracking) cols.push(`<td>${o.tracking || '-'}</td>`);
      if (printColumns.customerName) cols.push(`<td>${o.customer_name || '-'}</td>`);
      if (printColumns.country) cols.push(`<td>${o.detected_country || '-'}</td>`);
      if (printColumns.product) cols.push(`<td>${o.second_name || o.product || '-'}</td>`);
      if (printColumns.qty) cols.push(`<td>${o.qty || '-'}</td>`);
      if (printColumns.medicineCost) cols.push(`<td style="text-align: right;">₹${(o.medicine_cost || 0).toFixed(2)}</td>`);
      if (printColumns.weight) cols.push(`<td style="text-align: right;">${(o.product_weight || 0).toFixed(0)}g</td>`);
      if (printColumns.shippingCost) cols.push(`<td style="text-align: right;">₹${(o.shipping_cost || 0).toFixed(2)}</td>`);
      if (printColumns.total) cols.push(`<td style="text-align: right; font-weight: bold;">₹${(o.total_cost || 0).toFixed(2)}</td>`);
      if (printColumns.errors) cols.push(`<td class="error-text">${(o.errors || []).join(', ') || '-'}</td>`);
      return `<tr class="${o.errors && o.errors.length > 0 ? 'error-row' : ''}">${cols.join('')}</tr>`;
    }).join('');

    const totalMedicine = dataToPrint.reduce((sum, o) => sum + (o.medicine_cost || 0), 0);
    const totalShipping = dataToPrint.filter(o => o.is_order_first_row !== false).reduce((sum, o) => sum + (o.shipping_cost || 0), 0);
    const totalCost = dataToPrint.filter(o => o.is_order_first_row !== false).reduce((sum, o) => sum + (o.total_cost || 0), 0);

    // Build dynamic header columns
    const headerCols = ['<th>#</th>'];
    if (printColumns.client) headerCols.push('<th>Client</th>');
    if (printColumns.date) headerCols.push('<th>Date</th>');
    if (printColumns.service) headerCols.push('<th>Service</th>');
    if (printColumns.tracking) headerCols.push('<th>Tracking</th>');
    if (printColumns.customerName) headerCols.push('<th>Customer</th>');
    if (printColumns.country) headerCols.push('<th>Country</th>');
    if (printColumns.product) headerCols.push('<th>Product</th>');
    if (printColumns.qty) headerCols.push('<th>Qty</th>');
    if (printColumns.medicineCost) headerCols.push('<th>Med. Cost</th>');
    if (printColumns.weight) headerCols.push('<th>Weight</th>');
    if (printColumns.shippingCost) headerCols.push('<th>Ship. Cost</th>');
    if (printColumns.total) headerCols.push('<th>Total</th>');
    if (printColumns.errors) headerCols.push('<th>Errors</th>');
    
    // Count non-cost columns for totals row colspan
    const preCostColCount = 1 + (printColumns.client?1:0) + (printColumns.date?1:0) + (printColumns.service?1:0) + (printColumns.tracking?1:0) + (printColumns.customerName?1:0) + (printColumns.country?1:0) + (printColumns.product?1:0) + (printColumns.qty?1:0);

    // Build totals row
    const totalsCols = [`<td colspan="${preCostColCount}" style="text-align: right;">TOTALS:</td>`];
    if (printColumns.medicineCost) totalsCols.push(`<td style="text-align: right;">₹${totalMedicine.toFixed(2)}</td>`);
    if (printColumns.weight) totalsCols.push('<td></td>');
    if (printColumns.shippingCost) totalsCols.push(`<td style="text-align: right;">₹${totalShipping.toFixed(2)}</td>`);
    if (printColumns.total) totalsCols.push(`<td style="text-align: right;">₹${totalCost.toFixed(2)}</td>`);
    if (printColumns.errors) totalsCols.push('<td></td>');

    const uniqueOrders = new Set(dataToPrint.map(o => (o.tracking || '').toUpperCase()).filter(Boolean)).size;

    // Build the same visible-column data as JSON so the popup can export to Excel
    // without re-fetching / re-computing. Mirrors headerCols + row cells above.
    const excelHeaders = [];
    excelHeaders.push('#');
    if (printColumns.client) excelHeaders.push('Client');
    if (printColumns.date) excelHeaders.push('Date');
    if (printColumns.service) excelHeaders.push('Service');
    if (printColumns.tracking) excelHeaders.push('Tracking');
    if (printColumns.customerName) excelHeaders.push('Customer');
    if (printColumns.country) excelHeaders.push('Country');
    if (printColumns.product) excelHeaders.push('Product');
    if (printColumns.qty) excelHeaders.push('Qty');
    if (printColumns.medicineCost) excelHeaders.push('Medicine Cost');
    if (printColumns.weight) excelHeaders.push('Weight (g)');
    if (printColumns.shippingCost) excelHeaders.push('Shipping Cost');
    if (printColumns.total) excelHeaders.push('Total');
    if (printColumns.errors) excelHeaders.push('Errors');

    const excelRows = dataToPrint.map((o, idx) => {
      const row = [idx + 1];
      if (printColumns.client) row.push(o.client_code || '');
      if (printColumns.date) row.push(formatDMY(o.order_date));
      if (printColumns.service) row.push(o.detected_service || '');
      if (printColumns.tracking) row.push(o.tracking || '');
      if (printColumns.customerName) row.push(o.customer_name || '');
      if (printColumns.country) row.push(o.detected_country || '');
      if (printColumns.product) row.push(o.second_name || o.product || '');
      if (printColumns.qty) row.push(o.qty ?? '');
      if (printColumns.medicineCost) row.push(Number((o.medicine_cost || 0).toFixed(2)));
      if (printColumns.weight) row.push(Number((o.product_weight || 0).toFixed(0)));
      if (printColumns.shippingCost) row.push(Number((o.shipping_cost || 0).toFixed(2)));
      if (printColumns.total) row.push(Number((o.total_cost || 0).toFixed(2)));
      if (printColumns.errors) row.push((o.errors || []).join(', '));
      return row;
    });

    // Totals row (mirrors on-screen)
    const excelTotals = [''];
    const totalPad = (excelHeaders.length - 1) - (
      (printColumns.medicineCost?1:0) + (printColumns.weight?1:0) + (printColumns.shippingCost?1:0) + (printColumns.total?1:0) + (printColumns.errors?1:0)
    );
    for (let i = 0; i < totalPad - 1; i++) excelTotals.push('');
    excelTotals.push('TOTALS:');
    if (printColumns.medicineCost) excelTotals.push(Number(totalMedicine.toFixed(2)));
    if (printColumns.weight) excelTotals.push('');
    if (printColumns.shippingCost) excelTotals.push(Number(totalShipping.toFixed(2)));
    if (printColumns.total) excelTotals.push(Number(totalCost.toFixed(2)));
    if (printColumns.errors) excelTotals.push('');

    const excelMetaLines = [
      `${printSettings.companyName} - Order Cost Sheet`,
      `Generated: ${formatDMY(new Date())}${dateRangeText ? ` | Period: ${dateRangeText}` : ''} | Total Orders: ${uniqueOrders} | Total Products: ${dataToPrint.length}`,
      filterText,
    ];

    // Safe filename tag (strip anything but alnum-dash-underscore)
    const safeCompany = String(printSettings.companyName || 'Order-Cost')
      .replace(/[^A-Za-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'Order-Cost';
    const excelFilename = `${safeCompany}_Order-Cost-Sheet_${new Date().toISOString().slice(0,10)}.xlsx`;

    const excelPayload = {
      metaLines: excelMetaLines,
      headers: excelHeaders,
      rows: excelRows,
      totals: excelTotals,
      filename: excelFilename,
    };

    // Document content to be printed
    const documentContent = `
      ${(printShowCompanyName || printShowHeader) ? `
      <div class="header">
        ${printShowCompanyName ? `<h1>${printSettings.companyName} - Order Cost Sheet</h1>` : ''}
        ${printShowHeader ? `<p>Generated: ${formatDMY(new Date())}${dateRangeText ? ` | Period: ${dateRangeText}` : ''} | Total Orders: ${uniqueOrders} | Total Products: ${dataToPrint.length}</p>
        <div class="filters">${filterText}</div>` : ''}
      </div>
      ` : ''}
      ${printShowSummary ? `
      <div class="summary">
        <div class="summary-item">
          <div>Medicine Cost</div>
          <div class="value">₹${totalMedicine.toFixed(2)}</div>
        </div>
        <div class="summary-item">
          <div>Shipping Cost</div>
          <div class="value">₹${totalShipping.toFixed(2)}</div>
        </div>
        <div class="summary-item">
          <div>Total Cost</div>
          <div class="value">₹${totalCost.toFixed(2)}</div>
        </div>
      </div>
      ` : ''}
      <table>
        <thead>
          <tr>${headerCols.join('')}</tr>
        </thead>
        <tbody>
          ${tableRows}
          <tr class="totals">${totalsCols.join('')}</tr>
        </tbody>
      </table>
    `;

    // Print preview with toolbar (preview-first approach)
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Print Preview - ${printSettings.companyName} Order Cost Sheet</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: #f1f5f9; min-height: 100vh; }
          
          /* Preview Toolbar */
          .preview-toolbar {
            position: sticky;
            top: 0;
            z-index: 100;
            background: linear-gradient(to right, ${printSettings.headerColor}, ${printSettings.headerColor}99);
            padding: 12px 24px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }
          .preview-toolbar h1 {
            color: white;
            font-size: 18px;
            font-weight: 600;
          }
          .preview-toolbar .subtitle {
            color: rgba(255,255,255,0.8);
            font-size: 12px;
            margin-top: 2px;
          }
          .toolbar-buttons {
            display: flex;
            gap: 10px;
          }
          .toolbar-btn {
            padding: 10px 20px;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            transition: all 0.2s;
          }
          .btn-print {
            background: white;
            color: #4338CA;
          }
          .btn-print:hover {
            background: #f0f9ff;
            transform: translateY(-1px);
          }
          .btn-pdf {
            background: #059669;
            color: white;
          }
          .btn-pdf:hover {
            background: #047857;
            transform: translateY(-1px);
          }
          .btn-excel {
            background: #1D6F42;
            color: white;
          }
          .btn-excel:hover {
            background: #155a34;
            transform: translateY(-1px);
          }
          .btn-close {
            background: rgba(255,255,255,0.2);
            color: white;
          }
          .btn-close:hover {
            background: rgba(255,255,255,0.3);
          }
          
          /* Preview Container */
          .preview-container {
            padding: 24px;
            display: flex;
            justify-content: center;
          }
          .preview-page {
            background: white;
            box-shadow: 0 4px 20px rgba(0,0,0,0.1);
            border-radius: 8px;
            max-width: 1200px;
            width: 100%;
            overflow: hidden;
          }
          .preview-content {
            padding: 40px;
          }
          
          /* Print tip */
          .print-tip {
            background: #fef3c7;
            border: 1px solid #f59e0b;
            color: #92400e;
            padding: 10px 16px;
            margin: 16px 24px;
            border-radius: 8px;
            font-size: 13px;
            display: flex;
            align-items: center;
            gap: 8px;
          }
          
          /* Document Styles */
          ${styles}
          
          /* Hide toolbar when printing */
          @media print {
            .preview-toolbar, .print-tip { display: none !important; }
            .preview-container { padding: 0; }
            .preview-page { box-shadow: none; border-radius: 0; }
            body { background: white; }
          }
        </style>
        <script src="https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js"></script>
        <script>
          // Print function that keeps window open after print/cancel
          function handlePrint() {
            window.print();
            // Window stays open - user must manually close it
          }

          // Data payload for Excel export — injected from React parent so we
          // keep the on-screen columns and totals in perfect sync with the xlsx.
          const EXCEL_PAYLOAD = ${JSON.stringify(excelPayload)};

          function handleExcelExport() {
            try {
              if (typeof XLSX === 'undefined') {
                alert('Excel library still loading — please try again in a second.');
                return;
              }
              const wsData = [];
              (EXCEL_PAYLOAD.metaLines || []).forEach(line => wsData.push([line]));
              wsData.push([]); // spacer
              wsData.push(EXCEL_PAYLOAD.headers);
              (EXCEL_PAYLOAD.rows || []).forEach(r => wsData.push(r));
              wsData.push([]);
              if (EXCEL_PAYLOAD.totals) wsData.push(EXCEL_PAYLOAD.totals);

              const ws = XLSX.utils.aoa_to_sheet(wsData);
              // Column widths — auto-fit up to a reasonable max
              const colCount = EXCEL_PAYLOAD.headers.length;
              const colWidths = [];
              for (let c = 0; c < colCount; c++) {
                let maxLen = String(EXCEL_PAYLOAD.headers[c] || '').length;
                for (const r of EXCEL_PAYLOAD.rows) {
                  const cell = r[c] == null ? '' : String(r[c]);
                  if (cell.length > maxLen) maxLen = cell.length;
                }
                colWidths.push({ wch: Math.min(Math.max(maxLen + 2, 8), 40) });
              }
              ws['!cols'] = colWidths;

              const wb = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(wb, ws, 'Order Cost Sheet');
              XLSX.writeFile(wb, EXCEL_PAYLOAD.filename || 'order-cost-sheet.xlsx');
            } catch (err) {
              alert('Failed to export Excel: ' + (err && err.message ? err.message : err));
            }
          }
        </script>
      </head>
      <body>
        <div class="preview-toolbar">
          <div>
            <h1>🖨️ Print Preview</h1>
            <div class="subtitle">${printSettings.companyName} - Order Cost Sheet</div>
          </div>
          <div class="toolbar-buttons">
            <button class="toolbar-btn btn-close" onclick="window.close()">
              ✕ Close
            </button>
            <button class="toolbar-btn btn-excel" onclick="handleExcelExport()" data-testid="print-preview-excel-btn">
              📊 Excel
            </button>
            <button class="toolbar-btn btn-pdf" onclick="handlePrint()">
              📄 Save as PDF
            </button>
            <button class="toolbar-btn btn-print" onclick="handlePrint()">
              🖨️ Print
            </button>
          </div>
        </div>
        
        <div class="print-tip">
          💡 <strong>Tip:</strong> Review the content below. Click "Print" to send to printer, "Save as PDF" to download a PDF, or "Excel" to download an .xlsx with the same filtered rows. The preview stays open.
        </div>
        
        <div class="preview-container">
          <div class="preview-page">
            <div class="preview-content">
              ${documentContent}
            </div>
          </div>
        </div>
      </body>
      </html>
    `);
    printWindow.document.close();
    setShowPrintDialog(false);
  };

  // Clear data (UI only - does not delete from database)
  const handleClear = () => {
    setOrders([]);
    setSummary(null);
    setSheetsProcessed([]);
    setSearchTerm("");
    setFilterErrors(false);
    setCurrentPage(1);
  };

  // Get today's date password (DDMM format)
  const getTodayPassword = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    return `${day}${month}`;
  };

  // Verify wipe password
  const verifyWipePassword = () => {
    const correctPassword = getTodayPassword();
    if (wipePassword === correctPassword) {
      // EXTRA SAFETY: For "whole" wipe, require typing "DELETE ALL DATA"
      if (wipeOption === "whole") {
        const confirmText = window.prompt(
          '⚠️ DANGER: You are about to DELETE ALL ORDER DATA!\n\n' +
          'This action CANNOT be undone.\n\n' +
          'Type "DELETE ALL DATA" (without quotes) to confirm:'
        );
        if (confirmText !== "DELETE ALL DATA") {
          toast.error("Wipe cancelled - confirmation text did not match");
          resetWipeDialog();
          return;
        }
      }
      setWipePasswordError("");
      handleWipeData();
    } else {
      setWipePasswordError("Incorrect password. Please enter today's date in DDMM format.");
      setWipePassword("");
    }
  };

  // Reset wipe dialog state
  const resetWipeDialog = () => {
    setShowWipeDialog(false);
    setWipeStep(1);
    setWipePassword("");
    setWipePasswordError("");
    setWipeOption("today");
    setWipeDateFrom("");
    setWipeDateTo("");
    setWipeMonth(new Date().getMonth() + 1);
    setWipeYear(new Date().getFullYear());
    setWipeIncludeSales(false);
    setWipePreview(null);
    setWipePreviewError("");
  };

  // Build the wipe-request body (also used by preview + actual wipe)
  const buildWipePayload = (includeConfirm = false) => {
    const body = { wipe_option: wipeOption, include_sales: wipeIncludeSales };
    if (includeConfirm) body.confirm_code = "CONFIRM_WIPE";
    if (wipeOption === "date_range") {
      body.date_from = wipeDateFrom;
      body.date_to = wipeDateTo;
    } else if (wipeOption === "month") {
      body.month = Number(wipeMonth);
      body.year = Number(wipeYear);
    }
    return body;
  };

  // Fetch server-side preview whenever relevant inputs change and the dialog is on step 1
  useEffect(() => {
    if (!showWipeDialog || wipeStep !== 1) return;
    const needsRange = wipeOption === "date_range" && (!wipeDateFrom || !wipeDateTo);
    const needsMonth = wipeOption === "month" && (!wipeMonth || !wipeYear);
    if (needsRange || needsMonth) {
      setWipePreview(null);
      setWipePreviewError("");
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        setWipePreviewLoading(true);
        setWipePreviewError("");
        const resp = await fetch(`${BACKEND_URL}/api/shipment-costing/wipe-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(buildWipePayload(false)),
        });
        const data = await resp.json();
        if (!cancelled) {
          if (resp.ok) {
            setWipePreview(data);
          } else {
            setWipePreview(null);
            setWipePreviewError(data.detail || "Preview failed");
          }
        }
      } catch (_e) {
        if (!cancelled) {
          setWipePreview(null);
          setWipePreviewError("Preview failed");
        }
      } finally {
        if (!cancelled) setWipePreviewLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWipeDialog, wipeStep, wipeOption, wipeDateFrom, wipeDateTo, wipeMonth, wipeYear, wipeIncludeSales]);

  // Wipe data handler
  const handleWipeData = async () => {
    if (!isSuperAdmin) {
      toast.error("Only Super Admin can wipe data");
      return;
    }

    setWiping(true);

    try {
      // Call backend to wipe data from database
      // CRITICAL: confirm_code is required to prevent accidental wipes
      const response = await fetch(`${BACKEND_URL}/api/shipment-costing/wipe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(buildWipePayload(true))
      });

      const data = await response.json();

      if (response.ok) {
        if (wipeOption === "whole" || wipeOption === "whole_with_sales") {
          setOrders([]);
          setSummary(null);
          setSheetsProcessed([]);
          setSearchTerm("");
          setFilterErrors(false);
          setCurrentPage(1);
          toast.success(data.message || "Data wiped successfully");
        } else {
          // Refresh data from backend for today / date_range / month
          const refreshResponse = await fetch(`${BACKEND_URL}/api/shipment-costing/orders`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (refreshResponse.ok) {
            const refreshData = await refreshResponse.json();
            setOrders(refreshData.orders || []);
            setSummary(refreshData.summary);
            setSheetsProcessed(refreshData.sheets_processed || []);
          }
          toast.success(data.message || `Wiped ${data.deleted_orders || data.deleted || 0} orders`);
        }

        resetWipeDialog();
      } else {
        toast.error(data.detail || "Failed to wipe data");
      }
    } catch (error) {
      toast.error("Failed to wipe data");
      console.error(error);
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="space-y-2 animate-fade-in" data-testid="shipment-costing-page">
      {/* Floating recalc-progress card (only shown while a background job is
          running or just after it completes so the user sees the final tally). */}
      {recalcProgress && (
        <div
          data-testid="recalc-progress-card"
          className="fixed bottom-4 right-4 z-50 w-80 rounded-lg border border-purple-200 bg-white shadow-xl p-3 space-y-2"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Calculator className={`w-4 h-4 text-purple-600 ${!recalcProgress.done ? 'animate-pulse' : ''}`} />
              <p className="text-xs font-semibold text-slate-800">
                {recalcProgress.done
                  ? (recalcProgress.phase === 'cancelled' ? "Recalculation Cancelled" : "Recalculation Complete")
                  : recalcProgress.phase === 'counting'
                    ? "Counting orders…"
                    : recalcProgress.phase === 'loading_orders'
                      ? `Loading orders${recalcProgress.loaded ? ` (${recalcProgress.loaded.toLocaleString()}/${(recalcProgress.total || 0).toLocaleString()})` : ''}…`
                      : recalcProgress.phase === 'refreshing_parcels'
                        ? `Refreshing parcel totals${recalcProgress.touched_parcels ? ` (${(recalcProgress.parcel_refreshes || 0).toLocaleString()}/${recalcProgress.touched_parcels.toLocaleString()})` : ''}…`
                        : (recalcProgress.cancel_requested ? "Cancelling…" : (recalcProgress.client_code ? `Recalculating ${recalcProgress.client_code}` : "Recalculating Prices"))}
              </p>
            </div>
            <div className="flex items-center gap-1">
              {!recalcProgress.done && !recalcProgress.cancel_requested && (
                <button
                  data-testid="recalc-progress-cancel"
                  onClick={cancelRecalc}
                  className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 hover:bg-red-200 font-semibold flex items-center gap-1"
                  title="Cancel — stops immediately at the next row"
                >
                  <X className="w-3 h-3" />
                  Stop
                </button>
              )}
              {recalcProgress.done && (
                <button
                  data-testid="recalc-progress-dismiss"
                  onClick={() => setRecalcProgress(null)}
                  className="text-slate-400 hover:text-slate-700"
                  title="Dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
            <div
              className={`h-1.5 transition-all duration-500 ${
                recalcProgress.phase === 'cancelled'
                  ? 'bg-red-500'
                  : recalcProgress.done
                    ? 'bg-emerald-500'
                    : 'bg-purple-500'
              }`}
              style={{
                width: recalcProgress.total
                  ? `${Math.min(100, Math.round((recalcProgress.processed / recalcProgress.total) * 100))}%`
                  : '5%',
              }}
            />
          </div>
          <div className="flex items-center justify-between text-[11px] text-slate-600 tabular-nums">
            <span data-testid="recalc-progress-counts">
              {recalcProgress.processed?.toLocaleString?.() || 0} / {recalcProgress.total?.toLocaleString?.() || '?'}
            </span>
            <span>
              {recalcProgress.total
                ? `${Math.round((recalcProgress.processed / recalcProgress.total) * 100)}%`
                : ''}
            </span>
          </div>
          {recalcProgress.updated > 0 && (
            <p className="text-[11px] text-slate-500">
              ✓ <span className="font-medium text-slate-700">{recalcProgress.updated}</span> orders updated
              {recalcProgress.cancelled > 0 && (
                <> · <span className="text-red-500">{recalcProgress.cancelled}</span> cancelled fixed</>
              )}
            </p>
          )}
          {((recalcProgress.branded_priced || 0) + (recalcProgress.flat_priced || 0)) > 0 && (
            <div
              data-testid="recalc-progress-breakdown"
              className="flex items-center gap-3 text-[11px] text-slate-600"
            >
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                <span className="font-medium text-slate-700">
                  {(recalcProgress.branded_priced || 0).toLocaleString()}
                </span>
                <span className="text-slate-500">branded (date-priced)</span>
              </span>
              <span className="inline-flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                <span className="font-medium text-slate-700">
                  {(recalcProgress.flat_priced || 0).toLocaleString()}
                </span>
                <span className="text-slate-500">flat</span>
              </span>
            </div>
          )}
          {recalcProgress.done && recalcProgress.message && (
            <p className={`text-[11px] rounded px-2 py-1 border ${
              recalcProgress.phase === 'cancelled'
                ? 'text-red-700 bg-red-50 border-red-200'
                : 'text-emerald-700 bg-emerald-50 border-emerald-200'
            }`}>
              {recalcProgress.message}
            </p>
          )}
        </div>
      )}
      {/* Compact Header Bar */}
      <div className="flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
            <Calculator className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-foreground leading-tight">Shipment Costing</h1>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              {orders.length > 0 ? (
                <>
                  <span className="font-medium text-purple-600">{filteredOrders.length}</span> orders
                  {filteredDateRange && (
                    <span className="text-slate-500 font-medium">| {filteredDateRange.from} to {filteredDateRange.to}</span>
                  )}
                  {filterErrors && <span className="text-red-500">(errors)</span>}
                  {searchTerm && <span className="text-blue-500">&quot;{searchTerm}&quot;</span>}
                </>
              ) : (
                "Upload order sheet"
              )}
              {loadTime !== null && !loadingData && orders.length > 0 && (
                <Badge 
                  variant="outline" 
                  className={`text-[10px] h-4 px-1 ${loadTime < 200 ? 'border-green-400 text-green-600 bg-green-50' : loadTime < 500 ? 'border-yellow-400 text-yellow-600 bg-yellow-50' : 'border-red-400 text-red-600 bg-red-50'}`}
                  data-testid="load-time-badge"
                >
                  <Clock className="w-2.5 h-2.5 mr-0.5" />
                  {loadTime}ms
                </Badge>
              )}
            </p>
          </div>
        </div>

        {/* Action buttons - Compact toolbar */}
        <div className="flex flex-wrap items-center gap-1.5">
          {(orders.length > 0 || serverSearchTerm) && (
            <>
              {/* Server-side search (expanded to all fields).
                  Bound to `pendingSearchTerm` (user typing).
                  Server fetch only fires when:
                    • Enter is pressed
                    • Load button clicked
                    • ✕ clears the box
                  This stops the auto-fetch-on-every-keystroke behaviour
                  the user flagged as slow. */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-purple-400" />
                <Input
                  placeholder="Search all… (then Enter / Load)"
                  value={pendingSearchTerm}
                  onChange={(e) => setPendingSearchTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      setServerSearchTerm(pendingSearchTerm.trim());
                      setServerPage(1);
                    } else if (e.key === "Escape" && pendingSearchTerm) {
                      setPendingSearchTerm("");
                      setServerSearchTerm("");
                      setServerPage(1);
                    }
                  }}
                  className="pl-7 w-44 h-7 text-xs border-purple-300 focus:border-purple-500"
                  title="Type, then press Enter or click Load. ESC clears."
                  data-testid="shipment-costing-search-input"
                />
                {pendingSearchTerm && (
                  <button
                    onClick={() => {
                      setPendingSearchTerm("");
                      setServerSearchTerm("");
                      setServerPage(1);
                    }}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    title="Clear"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              {/* Load — commits the pending search to the server fetch.
                  Highlighted when there's an uncommitted change so the user
                  sees it's actionable. */}
              <Button
                size="sm"
                variant={pendingSearchTerm !== serverSearchTerm ? "default" : "outline"}
                onClick={() => {
                  setServerSearchTerm(pendingSearchTerm.trim());
                  setServerPage(1);
                }}
                disabled={pendingSearchTerm === serverSearchTerm}
                className={`h-7 px-3 text-xs ${pendingSearchTerm !== serverSearchTerm ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}`}
                title="Apply the typed search (Enter also works)"
                data-testid="shipment-costing-load-btn"
              >
                {pendingSearchTerm !== serverSearchTerm ? "↩ Load" : "Loaded"}
              </Button>
              
              {/* Recalculate searched tracking */}
              {serverSearchTerm && serverSearchTerm.length >= 3 && orders.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const trackingNumbers = [...new Set(
                      orders
                        .filter(o => o.tracking_number || o.tracking)
                        .map(o => o.tracking_number || o.tracking)
                    )];
                    handleRecalculateSelected(trackingNumbers);
                  }}
                  className="h-7 text-xs gap-1 border-green-400 text-green-700 hover:bg-green-50"
                  title="Recalculate costs for searched tracking numbers"
                >
                  <RefreshCw className="w-3 h-3" />
                  Recalc
                </Button>
              )}
              
              {/* Local filter */}
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  placeholder="Filter..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-7 w-28 h-7 text-xs"
                  title="Filter within currently loaded data"
                />
              </div>
              
              <Button
                variant={filterErrors ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterErrors(!filterErrors)}
                className={`h-7 text-xs gap-1 ${filterErrors ? 'bg-red-500 hover:bg-red-600' : 'border-red-300 text-red-600 hover:bg-red-50'}`}
              >
                <AlertTriangle className="w-3 h-3" />
                {realErrorCount}
              </Button>
              
              {/* Notifications Button with Dropdown */}
              <div className="relative group">
                <Button
                  variant={filterNotifications ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    if (filterNotificationType) {
                      setFilterNotificationType("");
                      setFilterNotifications(false);
                    } else {
                      setFilterNotifications(!filterNotifications);
                    }
                  }}
                  className={`h-7 text-xs gap-1 ${filterNotifications || filterNotificationType ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'border-emerald-300 text-emerald-600 hover:bg-emerald-50'}`}
                >
                  <CheckCircle2 className="w-3 h-3" />
                  {totalNotificationCount}
                  {filterNotificationType && (
                    <X 
                      className="w-3 h-3 ml-1 hover:text-white/80" 
                      onClick={(e) => {
                        e.stopPropagation();
                        setFilterNotificationType("");
                        setFilterNotifications(false);
                      }}
                    />
                  )}
                </Button>
                
                {/* Notification Types Dropdown */}
                {uniqueNotificationTypesWithCounts.length > 0 && (
                  <div className="absolute left-0 mt-1 w-72 bg-white border border-emerald-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="p-1.5 border-b border-emerald-100 bg-emerald-50/50">
                      <span className="text-xs font-medium text-emerald-700">Filter by Type</span>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {uniqueNotificationTypesWithCounts.map(({ type, count }) => (
                        <button
                          key={type}
                          onClick={() => {
                            setFilterNotificationType(type);
                            setFilterNotifications(true);
                          }}
                          className={`w-full px-2 py-1.5 text-left text-xs hover:bg-emerald-50 flex justify-between items-center ${
                            filterNotificationType === type ? 'bg-emerald-100 text-emerald-700' : 'text-slate-700'
                          }`}
                        >
                          <span className="flex items-center gap-1.5 text-emerald-600 truncate">
                            <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
                            {type}
                          </span>
                          <span className="text-[10px] bg-emerald-100 text-emerald-600 px-1.5 py-0.5 rounded-full ml-1">
                            {count}
                          </span>
                        </button>
                      ))}
                    </div>
                    {filterNotificationType && (
                      <div className="p-1.5 border-t border-emerald-100">
                        <button
                          onClick={() => {
                            setFilterNotificationType("");
                            setFilterNotifications(false);
                          }}
                          className="w-full text-xs text-emerald-600 hover:text-emerald-700"
                        >
                          Clear filter
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {/* Replaced Tracking Button */}
              {replacedTrackingCount > 0 && (
                <Button
                  variant={filterReplacedTracking ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterReplacedTracking(!filterReplacedTracking)}
                  className={`h-7 text-xs gap-1 ${filterReplacedTracking ? 'bg-violet-500 hover:bg-violet-600 text-white' : 'border-violet-300 text-violet-600 hover:bg-violet-50'}`}
                  title="View replaced tracking numbers"
                >
                  <RefreshCw className="w-3 h-3" />
                  {replacedTrackingCount}
                </Button>
              )}
              
              {/* Date filters - show only if count > 0 */}
              {withoutDateCount > 0 && (
                <Button
                  variant={filterWithoutDate ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterWithoutDate(!filterWithoutDate)}
                  className={`h-7 text-xs gap-1 ${filterWithoutDate ? 'bg-orange-500 hover:bg-orange-600 text-white' : 'border-orange-300 text-orange-600 hover:bg-orange-50'}`}
                  title="Orders without dates"
                >
                  <CalendarIcon className="w-3 h-3" />
                  No Date ({withoutDateCount})
                </Button>
              )}
              
              {autoAssignedDateCount > 0 && (
                <Button
                  variant={filterAutoAssignedDate ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilterAutoAssignedDate(!filterAutoAssignedDate)}
                  className={`h-7 text-xs gap-1 ${filterAutoAssignedDate ? 'bg-blue-500 hover:bg-blue-600 text-white' : 'border-blue-300 text-blue-600 hover:bg-blue-50'}`}
                  title="Orders with auto-assigned dates"
                >
                  <CalendarIcon className="w-3 h-3" />
                  Auto ({autoAssignedDateCount})
              </Button>
              )}
              
              <div className="w-px h-5 bg-slate-300" />
              
              {/* Action buttons - grouped */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                className="h-7 text-xs gap-1 border-emerald-300 hover:bg-emerald-50"
                title="Export to Excel"
              >
                <FileSpreadsheet className="w-3 h-3 text-emerald-500" />
                Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportGoogleSheets}
                className="h-7 text-xs gap-1 border-blue-300 hover:bg-blue-50"
                title="Export to CSV"
              >
                <FileText className="w-3 h-3 text-blue-500" />
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setHistoryOpen(true)}
                className="h-7 text-xs gap-1 border-amber-300 hover:bg-amber-50"
                title="View inline-edit history (with revert)"
                data-testid="open-edit-history-btn"
              >
                <Clock className="w-3 h-3 text-amber-600" />
                Edit History
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleFullDownload}
                className="h-7 text-xs gap-1 border-purple-300 hover:bg-purple-50"
                title="Full Download - Client, Customer, Country, Tracking, Product, Qty"
                data-testid="full-download-btn"
              >
                <Download className="w-3 h-3 text-purple-500" />
                Full Download
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowPrintDialog(true)}
                className="h-7 text-xs gap-1 border-orange-300 hover:bg-orange-50"
                title="Print preview"
              >
                <Printer className="w-3 h-3 text-orange-500" />
                Print
              </Button>
              <Button
                variant={showFilterPanel ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`h-7 text-xs gap-1 ${showFilterPanel ? 'bg-purple-600 text-white' : 'border-purple-300 hover:bg-purple-50'} ${hasActiveFilters ? 'ring-1 ring-purple-400' : ''}`}
              >
                <Filter className="w-3 h-3" />
                Filters
              </Button>
              
              {/* Toggle buttons in a dropdown menu */}
              <div className="relative group">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1"
                >
                  <Settings2 className="w-3 h-3" />
                  More
                </Button>
                <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <button
                    onClick={handleFixTrackingReplacements}
                    disabled={fixingTrackingReplacements}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-violet-50 flex items-center gap-2 text-violet-600"
                  >
                    <RefreshCw className={`w-3 h-3 ${fixingTrackingReplacements ? 'animate-spin' : ''}`} />
                    Fix Replacements
                  </button>
                  <button
                    onClick={() => setShowOrderSheetErrors(!showOrderSheetErrors)}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-amber-50 flex items-center gap-2 ${showOrderSheetErrors ? 'bg-amber-100 text-amber-700' : 'text-slate-700'}`}
                  >
                    {showOrderSheetErrors ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    Order Sheet Errors
                  </button>
                  <button
                    onClick={() => setShowCustomerColumn(!showCustomerColumn)}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-teal-50 flex items-center gap-2 ${showCustomerColumn ? 'bg-teal-100 text-teal-700' : 'text-slate-700'}`}
                    data-testid="toggle-customer-column"
                  >
                    {showCustomerColumn ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    Customer Column
                  </button>
                  <div className="border-t border-slate-200" />
                  <button
                    onClick={handleRefresh}
                    disabled={loadingData}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-blue-50 flex items-center gap-2 text-blue-600"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingData ? 'animate-spin' : ''}`} />
                    Refresh Data
                  </button>
                  <button
                    data-testid="recalculate-all-btn"
                    onClick={handleRecalculatePrices}
                    disabled={loading || (recalcProgress && !recalcProgress.done)}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-green-50 flex items-center gap-2 text-green-600 disabled:opacity-60"
                  >
                    <Calculator className={`w-3 h-3 ${(recalcProgress && !recalcProgress.done) ? 'animate-pulse' : ''}`} />
                    {recalcProgress && !recalcProgress.done
                      ? `Recalculating… ${recalcProgress.processed || 0}/${recalcProgress.total || '?'}`
                      : "Recalculate All"}
                  </button>
                  {filterClient && (
                    <button
                      data-testid="recalculate-client-btn"
                      onClick={() => handleRecalculatePrices({ clientCode: filterClient })}
                      disabled={loading || (recalcProgress && !recalcProgress.done)}
                      className="w-full px-3 py-2 text-left text-xs hover:bg-emerald-50 flex items-center gap-2 text-emerald-700 disabled:opacity-60"
                      title="Recalculate only orders for the current client-code filter (much faster)"
                    >
                      <Calculator className="w-3 h-3" />
                      Recalculate {filterClient} only
                    </button>
                  )}
                  <button
                    onClick={handleClear}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-slate-100 flex items-center gap-2 text-slate-600"
                  >
                    <X className="w-3 h-3" />
                    Clear View
                  </button>
                  <div className="border-t border-slate-200" />
                  <button
                    onClick={() => handleAutoGenerateDPSales()}
                    disabled={generatingDPSales}
                    className="w-full px-3 py-2 text-left text-xs hover:bg-green-50 flex items-center gap-2 text-green-600 font-medium"
                  >
                    <DollarSign className={`w-3 h-3 ${generatingDPSales ? 'animate-pulse' : ''}`} />
                    {generatingDPSales ? 'Generating...' : 'Auto-Generate DP Client Sales'}
                  </button>
                  <button
                    onClick={() => setShowDuplicateTrackingDialog(true)}
                    className={`w-full px-3 py-2 text-left text-xs hover:bg-rose-50 flex items-center gap-2 font-medium ${dupCounts.open > 0 ? "text-rose-600" : "text-slate-600"}`}
                    data-testid="open-duplicate-tracking-report"
                  >
                    <AlertTriangle className="w-3 h-3" />
                    <span className="flex-1">Duplicate Tracking Report</span>
                    {(dupCounts.open > 0 || dupCounts.resolved > 0) && (
                      <span
                        className="flex items-center gap-1"
                        data-testid="dup-menu-counts"
                      >
                        {dupCounts.open > 0 && (
                          <span className="inline-flex items-center px-1.5 py-0 rounded bg-rose-100 text-rose-700 text-[10px] font-semibold border border-rose-200">
                            {dupCounts.open} open
                          </span>
                        )}
                        {dupCounts.resolved > 0 && (
                          <span className="inline-flex items-center px-1.5 py-0 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold border border-emerald-200">
                            {dupCounts.resolved} done
                          </span>
                        )}
                      </span>
                    )}
                  </button>
                  {isSuperAdmin && (
                    <>
                      <div className="border-t border-slate-200" />
                      <button
                        onClick={() => setShowWipeDialog(true)}
                        className="w-full px-3 py-2 text-left text-xs hover:bg-red-50 flex items-center gap-2 text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                        Wipe Data
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
          
          {/* Packing List Upload - compact */}
          <div className="relative">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handlePackingListUpload}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={uploadingPacking}
            />
            <Button
              variant="outline"
              size="sm"
              className={`h-7 text-xs gap-1 ${packingListFile ? 'border-teal-400 bg-teal-50 text-teal-700' : 'border-teal-300 hover:bg-teal-50'}`}
              disabled={uploadingPacking}
            >
              {uploadingPacking ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <FileBox className="w-3 h-3 text-teal-500" />
              )}
              {packingListFile ? 'Packing ✓' : 'Packing'}
            </Button>
          </div>
          
          {/* Weight Source Toggle */}
          {packingListData && Object.keys(packingListData).length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowWeightSourceDialog(true)}
              className={`h-7 text-xs gap-1 ${weightSource === 'packing_list' ? 'border-cyan-400 bg-cyan-50 text-cyan-700' : 'border-slate-300'}`}
            >
              <Scale className="w-3 h-3" />
              {weightSource === 'packing_list' ? 'Pkg Wt' : 'Price Wt'}
            </Button>
          )}
          
          <Button
            onClick={() => setShowUploadDialog(true)}
            size="sm"
            className="h-7 text-xs gap-1 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white"
          >
            <Upload className="w-3 h-3" />
            Upload
          </Button>
        </div>
      </div>

      {/* Summary Bar - compact inline */}
      <SummaryCards summary={summary ? { ...summary, orders_with_errors: realErrorCount } : null} />

      {/* Date View Selector - Smart date filtering */}
      <DateViewSelector
        onDateRangeChange={(from, to) => {
          setFilterDateFrom(from);
          setFilterDateTo(to);
        }}
        orders={orders}
        compact={true}
        skipInitialFire={arrivedWithTracking.current}
        clientFilter={filterClient}
        onClientFilterChange={setFilterClient}
      />

      {/* Daily Overview Chart - inline collapsible */}
      <MonthlyChart
        dateFrom={filterDateFrom}
        dateTo={filterDateTo}
        token={token}
        onDayClick={(ddmmyy) => {
          setFilterDateFrom(ddmmyy);
          setFilterDateTo(ddmmyy);
        }}
      />

      {/* Advanced Filter Panel - compact */}
      {showFilterPanel && (
        <FilterPanel
          filterErrors={filterErrors}
          setFilterErrors={setFilterErrors}
          filterSuccessOnly={filterSuccessOnly}
          setFilterSuccessOnly={setFilterSuccessOnly}
          filterErrorType={filterErrorType}
          setFilterErrorType={setFilterErrorType}
          filterService={filterService}
          setFilterService={setFilterService}
          filterCountry={filterCountry}
          setFilterCountry={setFilterCountry}
          filterClient={filterClient}
          setFilterClient={setFilterClient}
          filterCostMin={filterCostMin}
          setFilterCostMin={setFilterCostMin}
          filterDateFrom={filterDateFrom}
          setFilterDateFrom={setFilterDateFrom}
          filterDateTo={filterDateTo}
          setFilterDateTo={setFilterDateTo}
          filterDpClientsOnly={filterDpClientsOnly}
          setFilterDpClientsOnly={setFilterDpClientsOnly}
          uniqueErrorTypesWithCounts={uniqueErrorTypesWithCounts}
          uniqueServices={uniqueServices}
          uniqueCountries={uniqueCountries}
          uniqueClients={uniqueClients}
          dpClientCodes={dpClientCodes}
          allLedgerClients={allLedgerClients}
          filteredOrdersCount={filteredOrders.length}
          totalOrdersCount={orders.length}
          dpClientOrdersCount={summary?.dp_client_orders || 0}
          hasActiveFilters={hasActiveFilters}
          clearFilters={clearFilters}
        />
      )}

      {/* Orders Table - maximized height */}
      {orders.length > 0 ? (
        <div className="border border-slate-200 rounded-lg overflow-hidden shadow-sm bg-white" data-testid="orders-table-container">
          <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-220px)]">
            <table className="w-full" data-testid="orders-table">
              <thead className="sticky top-0 z-10">
                <tr className="bg-gradient-to-r from-slate-800 to-slate-900">
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-100 w-14">Order#</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-100">Client</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-100 w-20">Date</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-100 w-20">Service</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-100">Tracking</th>
                  {showCustomerColumn && (
                    <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-100 bg-teal-700" title="Customer name">Customer</th>
                  )}
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-100 w-20">Country</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-100">Product</th>
                  <th className="px-2 py-2.5 text-center text-xs font-semibold uppercase text-slate-100 w-14">Qty</th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase text-slate-100 w-24">Med Cost</th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase text-slate-100 w-24 bg-green-700" title="Total medicine cost">Total Med</th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase text-slate-100 w-20">Row Wt</th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase text-slate-100 w-20 bg-amber-700">Gross Wt</th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase text-slate-100 w-24">Ship Cost</th>
                  <th className="px-2 py-2.5 text-right text-xs font-semibold uppercase text-slate-100 w-24 bg-purple-700">Total</th>
                  <th className="px-2 py-2.5 text-left text-xs font-semibold uppercase text-slate-100 w-28">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedOrders.map((order, idx) => {
                  // Get REAL errors (for row styling - not affected by toggle)
                  const realErrors = getRealErrors(order);
                  // Get DISPLAY errors (for showing - respects toggle for learning)
                  const displayErrors = getDisplayErrors(order);
                  // Row is an error row ONLY if it has REAL errors
                  const hasError = realErrors.length > 0;
                  const isTrackingRow = order.is_tracking_row || order.is_order_first_row;
                  const isMultiProductOrder = (order.order_row_count || order.parcel_product_count || 1) > 1;
                  const parcelProductCount = order.parcel_product_count || order.order_row_count || 1;
                  const isShiftedOrder = order.is_shifted || order.status === 'SHIFTED ORDER';
                  const isDuplicateOrder = order.is_duplicate || order.status === 'DUPLICATE';
                  const isReplacedTracking = order.is_strikethrough === true;
                  const replacementNote = order.replacement_note || (order.superseded_by ? `Replaced by ${order.superseded_by}` : '');
                  
                  return (
                    <tr
                      key={`${order.tracking}-${idx}`}
                      onDoubleClick={(e) => {
                        // Ignore double-clicks inside interactive elements
                        const tag = (e.target?.tagName || "").toLowerCase();
                        if (["a", "button", "input", "select", "textarea"].includes(tag)) return;
                        handleEditTrigger(order);
                      }}
                      title="Double-click to edit this row"
                      className={`transition-colors ${
                        isReplacedTracking
                          ? 'bg-violet-50/80 hover:bg-violet-100/80 opacity-70'
                          : isDuplicateOrder
                            ? 'bg-orange-50/80 hover:bg-orange-100/80 opacity-75'
                            : isShiftedOrder
                              ? 'bg-amber-50/70 hover:bg-amber-100/70 opacity-70'
                              : hasError 
                                ? 'bg-red-50 hover:bg-red-100' 
                                : isTrackingRow && isMultiProductOrder
                                  ? 'bg-blue-50 hover:bg-blue-100 border-l-4 border-l-blue-500'
                                  : !isTrackingRow && isMultiProductOrder
                                    ? 'bg-slate-50/50 hover:bg-slate-100/50 border-l-4 border-l-transparent'
                                    : isTrackingRow
                                      ? 'bg-white hover:bg-slate-50 border-l-4 border-l-green-500'
                                      : 'hover:bg-slate-50'
                      }`}
                      style={isReplacedTracking
                        ? { textDecoration: 'line-through', textDecorationColor: '#8b5cf6' }
                        : isDuplicateOrder 
                          ? { textDecoration: 'line-through', textDecorationColor: '#f97316' }
                          : isShiftedOrder 
                            ? { textDecoration: 'line-through', textDecorationColor: '#94a3b8' } 
                            : {}
                      }
                    >
                      {/* ORDER# */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 text-center font-medium">
                        {isTrackingRow && order.order_number ? (
                          <div className="flex flex-col items-center">
                            <span className="text-blue-600 font-bold text-base">{order.order_number}</span>
                            {isMultiProductOrder && (
                              <span className="text-[10px] text-blue-400">({parcelProductCount})</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {/* CLIENT */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 font-semibold text-slate-800">{order.client_code || '-'}</td>
                      {/* DATE */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 text-center font-mono">
                        {isTrackingRow && order.order_date ? (
                          <span className="text-slate-700 font-medium">{formatDMY(order.order_date)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {/* Service */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100">
                        {isTrackingRow ? (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            order.detected_service ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {order.detected_service || '-'}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {/* Tracking */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 font-mono text-blue-600">
                        {order.tracking || '-'}
                      </td>
                      {/* Customer Name */}
                      {showCustomerColumn && (
                        <td className="px-2 py-2 text-sm border-b border-slate-100 bg-teal-50">
                          {isTrackingRow ? (
                            <span className="text-teal-700 truncate block max-w-[140px]" title={order.customer_name || order.recipient_name || order.address || ''}>
                              {order.customer_name || order.recipient_name || order.address || '-'}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      )}
                      {/* Country */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100">
                        {isTrackingRow ? (
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            order.detected_country ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {order.detected_country || '-'}
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {/* Product - EDITABLE */}
                      <td 
                        className="px-2 py-2 text-sm border-b border-slate-100 cursor-pointer hover:bg-yellow-50"
                        onDoubleClick={() => handleCellDoubleClick(idx, 'second_name', order.second_name || order.product)}
                        title="Double-click to edit"
                      >
                        {editingCell?.rowIdx === idx && editingCell?.field === 'second_name' ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={() => handleEditSave(idx, 'second_name')}
                            onKeyDown={(e) => handleEditKeyDown(e, idx, 'second_name')}
                            autoFocus
                            className="w-full px-1 py-0.5 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                          />
                        ) : (
                          <span className="block truncate max-w-[180px]" title={order.second_name || order.product}>
                            {order.second_name || order.product || '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-sm border-b border-slate-100 text-center font-semibold">
                        {order.qty !== null && order.qty !== undefined ? order.qty : '-'}
                      </td>
                      {/* Medicine Cost */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 text-right font-mono">
                        {order.medicine_cost > 0 ? formatCurrency(order.medicine_cost) : '-'}
                      </td>
                      {/* TOTAL MED - Parcel total */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 text-right font-mono font-semibold bg-green-50">
                        {isTrackingRow ? (() => {
                          const totalMedCost = order.order_medicine_cost || order.parcel_medicine_cost || order.medicine_cost || 0;
                          return totalMedCost > 0 ? (
                            <span className="text-green-700">
                              {formatCurrency(totalMedCost)}
                            </span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          );
                        })() : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {/* Row Weight */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 text-right font-mono text-slate-600">
                        {order.product_weight > 0 ? `${order.product_weight}g` : '-'}
                      </td>
                      {/* GROSS WEIGHT */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 text-right font-mono font-semibold bg-amber-50">
                        {isTrackingRow ? (
                          order.gross_weight > 0 ? (
                            <span className="text-amber-700">{order.gross_weight}g</span>
                          ) : order.product_weight > 0 ? (
                            <span className="text-amber-700">{order.product_weight}g</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {/* SHIPPING COST */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 text-right font-mono">
                        {isTrackingRow && order.shipping_cost > 0 ? (
                          <span className="text-orange-600 font-semibold">{formatCurrency(order.shipping_cost)}</span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      {/* TOTAL */}
                      <td className="px-2 py-2 text-sm border-b border-slate-100 text-right font-mono font-bold bg-purple-50">
                        {isTrackingRow ? (() => {
                          const totalMedCost = order.order_medicine_cost || order.parcel_medicine_cost || order.medicine_cost || 0;
                          const shipCost = order.shipping_cost || 0;
                          const calculatedTotal = totalMedCost + shipCost;
                          return calculatedTotal > 0 ? (
                            <span className="text-purple-700">{formatCurrency(calculatedTotal)}</span>
                          ) : (
                            <span className="text-slate-300">—</span>
                          );
                        })() : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-sm border-b border-slate-100">
                        {hasError ? (
                          <div className="flex items-start gap-1">
                            <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            <span className="text-xs text-red-600 leading-tight truncate max-w-[100px]" title={realErrors.join(', ')}>
                              {realErrors[0]}
                            </span>
                            {realErrors[0]?.includes('Product not found') && (
                              <ProductSuggestButton errorText={realErrors[0]} token={token} onFixed={handleRefresh} />
                            )}
                          </div>
                        ) : displayErrors.length > 0 ? (
                          <div className="flex items-start gap-1.5 px-1.5 py-1 bg-amber-50 rounded border border-amber-200">
                            <Eye className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                            <span className="text-xs text-amber-700 leading-tight truncate max-w-[100px]" title={displayErrors.join(', ')}>
                              {displayErrors[0]}
                            </span>
                          </div>
                        ) : order.notifications && order.notifications.length > 0 ? (
                          <div className="flex items-start gap-1.5 px-1.5 py-1 bg-emerald-50 rounded border border-emerald-200">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                            <span className="text-xs text-emerald-700 leading-tight truncate max-w-[100px]" title={order.notifications.join(', ')}>
                              {order.notifications[0]}
                            </span>
                          </div>
                        ) : isReplacedTracking ? (
                          <div className="px-2 py-1 bg-violet-100 rounded border border-violet-300" title={replacementNote}>
                            <span className="text-xs font-bold text-violet-700 uppercase">
                              REPLACED
                            </span>
                          </div>
                        ) : isDuplicateOrder ? (
                          <div className="px-2 py-1 bg-orange-100 rounded border border-orange-300">
                            <span className="text-xs font-bold text-orange-700 uppercase">
                              DUPE
                            </span>
                          </div>
                        ) : isShiftedOrder ? (
                          <div className="px-2 py-1 bg-amber-100 rounded border border-amber-300">
                            <span className="text-xs font-bold text-amber-700 uppercase">
                              SHIFTED
                            </span>
                          </div>
                        ) : isTrackingRow ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Compact Pagination Footer */}
          {filteredOrders.length > 0 && (
            <div className="bg-gradient-to-r from-slate-800 to-slate-900 px-3 py-2 flex items-center justify-between">
              {/* Left - Server pagination info */}
              <div className="flex items-center gap-3 text-sm text-slate-300">
                <span><span className="text-purple-400 font-medium">DB:</span> {serverPagination.total_rows?.toLocaleString() || 0}</span>
                {serverPagination.total_pages > 1 && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setServerPage(p => Math.max(1, p - 1))}
                      disabled={serverPage === 1 || loadingData}
                      className="h-6 w-6 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
                    >
                      <ChevronLeft className="w-3 h-3" />
                    </Button>
                    <span className="text-purple-300 px-1.5 bg-purple-900/30 rounded text-[10px]">
                      {serverPage}/{serverPagination.total_pages}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setServerPage(p => Math.min(serverPagination.total_pages, p + 1))}
                      disabled={!serverPagination.has_more || loadingData}
                      className="h-6 w-6 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
                    >
                      <ChevronRight className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Center - Current view info */}
              <div className="text-xs text-slate-400">
                {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, filteredOrders.length)} of {filteredOrders.length}
                {hasActiveFilters && <span className="text-amber-400 ml-1">(filtered)</span>}
                {filteredDateRange && <span className="text-cyan-400 ml-1">| {filteredDateRange.from} to {filteredDateRange.to}</span>}
              </div>
              
              {/* Right - Page controls */}
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="h-6 px-2 text-xs text-slate-400 hover:text-white hover:bg-slate-700"
                >
                  First
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="h-6 w-6 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
                >
                  <ChevronLeft className="w-3 h-3" />
                </Button>
                <span className="text-xs text-slate-300 px-2">
                  {currentPage}/{totalPages || 1}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="h-6 w-6 p-0 text-slate-400 hover:text-white hover:bg-slate-700"
                >
                  <ChevronRight className="w-3 h-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="h-6 px-2 text-xs text-slate-400 hover:text-white hover:bg-slate-700"
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : loadingData ? (
        // Loading state
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50/50">
          <div className="max-w-xs mx-auto">
            <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-3">
              <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
            </div>
            <h3 className="text-base font-semibold text-slate-700 mb-1">Loading Data...</h3>
            <p className="text-xs text-slate-500">
              Checking for previously uploaded order data...
            </p>
          </div>
        </div>
      ) : (
        // Empty state - different message if search is active vs no data loaded
        <div className="border-2 border-dashed border-slate-200 rounded-xl p-8 text-center bg-slate-50/50">
          <div className="max-w-xs mx-auto">
            {serverSearchTerm ? (
              // Search returned no results
              <>
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-amber-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Results Found</h3>
                <p className="text-sm text-slate-500 mb-4">
                  No tracking numbers matching &quot;<span className="font-mono font-medium text-purple-600">{serverSearchTerm}</span>&quot; were found in the database.
                </p>
                <Button
                  onClick={() => { setServerSearchTerm(""); setPendingSearchTerm(""); setServerPage(1); }}
                  variant="outline"
                  className="gap-2"
                >
                  <X className="w-4 h-4" />
                  Clear Search
                </Button>
              </>
            ) : (
              // No data loaded at all
              <>
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
                  <Calculator className="w-8 h-8 text-purple-500" />
                </div>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Orders Loaded</h3>
                <p className="text-sm text-slate-500 mb-6">
                  Upload your order Excel file to automatically calculate medicine costs, shipping costs, and total landed cost for each order.
                </p>
                <Button
                  onClick={() => setShowUploadDialog(true)}
                  className="gap-2 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white"
                >
                  <Upload className="w-4 h-4" />
                  Upload Order Sheet
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Upload className="w-5 h-5 text-purple-500" />
              Upload Order Sheet
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Download Template Banner */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-6 h-6 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800 text-sm">Need the correct format?</p>
                  <p className="text-xs text-amber-600">Download template with sample data</p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="gap-1 bg-white border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => window.open(`${BACKEND_URL}/api/shipment-costing/download-template`, '_blank')}
                data-testid="download-order-template-btn"
              >
                <Download className="w-3 h-3" />
                Template
              </Button>
            </div>

            <p className="text-sm text-muted-foreground">
              Upload your order Excel file (.xlsx). The system will automatically:
            </p>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Detect service type (EMS, DHL, RMS, etc.) from address
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Detect country from address block
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Match products with Price List and calculate medicine cost
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Calculate product weight and shipping cost from Shipping Rates
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                Process multiple sheets (date-wise / service-wise tabs)
              </li>
            </ul>
            
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-purple-300 transition-colors">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleUpload}
                className="hidden"
                id="order-upload"
                disabled={uploading}
              />
              <label htmlFor="order-upload" className="cursor-pointer">
                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-purple-500 animate-spin" />
                    <span className="text-sm text-slate-600">{uploadProgressMessage || "Processing file..."}</span>
                    {/* Progress bar */}
                    {uploadProgress > 0 && (
                      <div className="w-full max-w-xs">
                        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-purple-500 transition-all duration-300 ease-out"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <span className="text-xs text-slate-500 mt-1 block text-center">{uploadProgress}% complete</span>
                      </div>
                    )}
                    {backgroundTaskId && (
                      <span className="text-xs text-blue-500">Large file - processing in background</span>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <FileSpreadsheet className="w-10 h-10 text-purple-400" />
                    <span className="text-sm text-slate-600">
                      Click to select or drag & drop Excel file
                    </span>
                    <span className="text-xs text-slate-400">
                      Supports .xlsx, .xls, and .xlsb (binary) files
                    </span>
                    <span className="text-xs text-purple-500">
                      Files &gt;6MB will be processed in background to avoid timeout
                    </span>
                    <span className="text-xs text-green-600">
                      💡 Tip: Use .xlsb format for 50-75% smaller files & faster processing
                    </span>
                  </div>
                )}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUploadDialog(false)} disabled={uploading}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Tracking Report Dialog */}
      <Dialog
        open={showDuplicateTrackingDialog}
        onOpenChange={(open) => {
          setShowDuplicateTrackingDialog(open);
          if (!open) {
            setDupReport(null);
            setDupReportError("");
          }
        }}
      >
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
          data-testid="duplicate-tracking-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" />
              Duplicate Tracking Report
            </DialogTitle>
            <DialogDescription>
              Cross-upload scan. Surfaces every tracking number that exists in
              more than one shipment with a different client, customer or country.
              These are usually operator typos -- fix them in the source sheet,
              re-upload, and the phantom parcels disappear.
            </DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2 py-2 border-b border-slate-200">
            <span className="text-xs text-slate-600">Scope:</span>
            <select
              value={dupReportScope.month}
              onChange={(e) => setDupReportScope((s) => ({ ...s, month: e.target.value }))}
              className="text-xs border border-slate-300 rounded px-2 py-1"
              data-testid="dup-report-month"
            >
              <option value="">All months</option>
              {["01","02","03","04","05","06","07","08","09","10","11","12"].map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
            <input
              type="number"
              value={dupReportScope.year}
              onChange={(e) => setDupReportScope((s) => ({ ...s, year: e.target.value }))}
              placeholder="Year"
              className="text-xs border border-slate-300 rounded px-2 py-1 w-24"
              data-testid="dup-report-year"
            />
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => fetchDuplicateTrackingReport(dupReportScope)}
              disabled={dupReportLoading}
              data-testid="dup-report-refresh"
            >
              {dupReportLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Refresh
            </Button>
            <label
              className="flex items-center gap-1 text-xs text-slate-600 ml-1 cursor-pointer"
              data-testid="dup-report-include-resolved"
            >
              <input
                type="checkbox"
                checked={dupIncludeResolved}
                onChange={(e) => setDupIncludeResolved(e.target.checked)}
                className="accent-rose-600"
              />
              Show resolved
            </label>
            {dupReport && (
              <div className="ml-auto text-xs text-slate-600">
                <span className="font-semibold text-rose-700">{dupReport.collisions.toLocaleString()}</span> collision{dupReport.collisions === 1 ? "" : "s"} · <span className="font-semibold">{dupReport.rows_affected.toLocaleString()}</span> rows
                {dupReport.truncated && <span className="ml-1 text-amber-600">(truncated)</span>}
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto -mx-6 px-6 py-2">
            {dupReportError && (
              <div className="text-sm px-3 py-2 rounded bg-rose-50 border border-rose-200 text-rose-700">
                {dupReportError}
              </div>
            )}
            {dupReportLoading && !dupReport && (
              <div className="flex items-center justify-center py-10 text-slate-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Scanning shipment_orders…
              </div>
            )}
            {dupReport && dupReport.collisions === 0 && !dupReportLoading && (
              <div
                data-testid="dup-report-empty"
                className="text-sm px-3 py-2 rounded bg-emerald-50 border border-emerald-200 text-emerald-700"
              >
                No duplicate-tracking collisions found in this scope.
              </div>
            )}
            {dupReport && dupReport.groups.length > 0 && (
              <div className="space-y-2">
                {dupReport.groups.map((g) => (
                  <div
                    key={g.tracking}
                    className="border border-rose-200 rounded-md bg-white"
                    data-testid={`dup-group-${g.tracking}`}
                  >
                    <div
                      className={`flex items-center justify-between px-3 py-1.5 border-b ${g.resolved ? "bg-emerald-50/50 border-emerald-100" : "bg-rose-50/50 border-rose-100"}`}
                    >
                      <div
                        className="flex-1 cursor-pointer hover:underline"
                        onClick={() => {
                          setSearchTerm(g.tracking);
                          setShowDuplicateTrackingDialog(false);
                          setTimeout(() => {
                            document.querySelector('[data-testid="orders-table"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
                          }, 100);
                          toast.success(`Filtered orders to tracking ${g.tracking}`);
                        }}
                        title="Click to filter the orders table to this tracking"
                        data-testid={`dup-group-jump-${g.tracking}`}
                      >
                        <div className={`font-mono text-sm font-semibold ${g.resolved ? "text-emerald-700" : "text-rose-700"}`}>
                          {g.tracking}{" "}
                          <span className="text-[10px] font-normal text-slate-500">(click to jump)</span>
                          {g.resolved && (
                            <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0 rounded bg-emerald-100 text-emerald-800 text-[9px] font-semibold border border-emerald-200">
                              RESOLVED
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                          {g.occurrence_count} occurrences · first {String(g.first_seen).slice(0, 10)} · last {String(g.last_seen).slice(0, 10)}
                          {g.resolved && (
                            <span className="ml-2 text-emerald-700">
                              · by {g.resolved_by || "—"} on {String(g.resolved_at).slice(0, 10)}
                              {g.resolved_note ? ` · “${g.resolved_note}”` : ""}
                            </span>
                          )}
                        </div>
                      </div>
                      <div onClick={(e) => e.stopPropagation()}>
                        {g.resolved ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-[11px] text-emerald-700 hover:bg-emerald-100"
                            onClick={() => unmarkDuplicateResolved(g.tracking)}
                            disabled={dupResolvingTracking === g.tracking}
                            data-testid={`dup-unresolve-${g.tracking}`}
                          >
                            {dupResolvingTracking === g.tracking ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              "Re-open"
                            )}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-[11px] border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => {
                              const note = window.prompt(
                                `Resolve "${g.tracking}"?\n\nOptional note:`,
                                ""
                              );
                              if (note !== null) markDuplicateResolved(g.tracking, note);
                            }}
                            disabled={dupResolvingTracking === g.tracking}
                            data-testid={`dup-resolve-${g.tracking}`}
                          >
                            {dupResolvingTracking === g.tracking ? (
                              <Loader2 className="w-3 h-3 animate-spin mr-1" />
                            ) : null}
                            Mark resolved
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="p-2 space-y-1">
                      {g.occurrences.map((o, i) => (
                        <div
                          key={i}
                          className="text-xs text-slate-700 grid grid-cols-[auto_auto_1fr_auto_auto] gap-x-2 items-center"
                        >
                          <span className="text-indigo-700 font-medium">{o.client_code || "—"}</span>
                          <span className="text-slate-500 font-mono">{formatDMY(o.order_date)}</span>
                          <span className="truncate">{o.customer_name || "(no name)"}</span>
                          <span className="text-slate-500">{o.country || "—"}</span>
                          <span
                            className="text-[10px] text-slate-400 font-mono"
                            title={`batch: ${o.upload_batch_id}\nuploaded: ${formatDMY(o.uploaded_at)}`}
                          >
                            {(o.upload_batch_id || "").slice(0, 8)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDuplicateTrackingDialog(false)}
              data-testid="dup-report-close"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Wipe Data Dialog - Super Admin Only */}
      <Dialog open={showWipeDialog} onOpenChange={(open) => { if (!open) resetWipeDialog(); else setShowWipeDialog(true); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="wipe-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2 text-red-600">
              <ShieldAlert className="w-5 h-5" />
              Wipe Costing Data
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              {wipeStep === 1 
                ? "This action will permanently delete the selected costing data. This cannot be undone."
                : "Enter security password to confirm wipe operation."
              }
            </DialogDescription>
          </DialogHeader>
          
          {wipeStep === 1 ? (
            /* Step 1: Confirmation and Option Selection */
            <>
              <div className="space-y-4 py-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Are you sure you want to wipe data?
                  </p>
                </div>

                <div className="space-y-3">
                  <p className="text-sm font-medium text-slate-700">Select wipe scope:</p>
                  <RadioGroup value={wipeOption} onValueChange={setWipeOption} className="space-y-2">
                    <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" data-testid="wipe-option-today">
                      <RadioGroupItem value="today" id="today" className="mt-0.5" />
                      <Label htmlFor="today" className="flex-1 cursor-pointer">
                        <span className="font-medium text-slate-800">Today&apos;s Upload</span>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Only orders uploaded today ({formatDMY(new Date())})
                        </p>
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" data-testid="wipe-option-date-range">
                      <RadioGroupItem value="date_range" id="date_range" className="mt-0.5" />
                      <Label htmlFor="date_range" className="flex-1 cursor-pointer space-y-2">
                        <div>
                          <span className="font-medium text-slate-800">Date Range (order date)</span>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Delete orders whose parcel/order date falls in this window.
                          </p>
                        </div>
                        {wipeOption === "date_range" && (
                          <div
                            className="grid grid-cols-2 gap-2 pt-1"
                            // Stop the parent Label from forwarding clicks to the
                            // radio (which was canceling the calendar popover).
                            onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
                          >
                            <div>
                              <Label className="text-[11px] text-slate-500">From</Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="h-8 w-full justify-start text-left font-normal text-xs px-2"
                                    data-testid="wipe-date-from"
                                  >
                                    <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                                    {wipeDateFrom
                                      ? formatDate(parseISO(wipeDateFrom), "dd/MM/yyyy")
                                      : <span className="text-slate-400">dd/mm/yyyy</span>}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={wipeDateFrom ? parseISO(wipeDateFrom) : undefined}
                                    onSelect={(d) => {
                                      if (d) setWipeDateFrom(formatDate(d, "yyyy-MM-dd"));
                                    }}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                            <div>
                              <Label className="text-[11px] text-slate-500">To</Label>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    className="h-8 w-full justify-start text-left font-normal text-xs px-2"
                                    data-testid="wipe-date-to"
                                  >
                                    <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
                                    {wipeDateTo
                                      ? formatDate(parseISO(wipeDateTo), "dd/MM/yyyy")
                                      : <span className="text-slate-400">dd/mm/yyyy</span>}
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <Calendar
                                    mode="single"
                                    selected={wipeDateTo ? parseISO(wipeDateTo) : undefined}
                                    onSelect={(d) => {
                                      if (d) setWipeDateTo(formatDate(d, "yyyy-MM-dd"));
                                    }}
                                    disabled={(d) =>
                                      wipeDateFrom ? d < parseISO(wipeDateFrom) : false
                                    }
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        )}
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer" data-testid="wipe-option-month">
                      <RadioGroupItem value="month" id="month" className="mt-0.5" />
                      <Label htmlFor="month" className="flex-1 cursor-pointer space-y-2">
                        <div>
                          <span className="font-medium text-slate-800">Monthly</span>
                          <p className="text-xs text-slate-500 mt-0.5">
                            Delete every order for the selected month/year.
                          </p>
                        </div>
                        {wipeOption === "month" && (
                          <div className="grid grid-cols-2 gap-2 pt-1" onClick={(e) => e.preventDefault()}>
                            <div>
                              <Label className="text-[11px] text-slate-500">Month</Label>
                              <select
                                value={wipeMonth}
                                onChange={(e) => setWipeMonth(Number(e.target.value))}
                                className="h-8 w-full rounded-md border border-slate-200 text-xs px-2"
                                data-testid="wipe-month-select"
                              >
                                {['January','February','March','April','May','June','July','August','September','October','November','December'].map((n, i) => (
                                  <option key={n} value={i + 1}>{n}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <Label className="text-[11px] text-slate-500">Year</Label>
                              <Input
                                type="number"
                                min={2020}
                                max={2099}
                                value={wipeYear}
                                onChange={(e) => setWipeYear(Number(e.target.value) || new Date().getFullYear())}
                                className="h-8 text-xs"
                                data-testid="wipe-year-input"
                              />
                            </div>
                          </div>
                        )}
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer border-red-200" data-testid="wipe-option-whole">
                      <RadioGroupItem value="whole" id="whole" className="mt-0.5" />
                      <Label htmlFor="whole" className="flex-1 cursor-pointer">
                        <span className="font-medium text-red-600">Wipe Orders Only</span>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Remove ALL costing data ({orders.length.toLocaleString()} orders shown). Sales/Ledger remain.
                        </p>
                      </Label>
                    </div>

                    <div className="flex items-start space-x-3 p-3 border rounded-lg hover:bg-red-50 cursor-pointer border-red-300 bg-red-50/50" data-testid="wipe-option-whole-with-sales">
                      <RadioGroupItem value="whole_with_sales" id="whole_with_sales" className="mt-0.5" />
                      <Label htmlFor="whole_with_sales" className="flex-1 cursor-pointer">
                        <span className="font-medium text-red-700">Wipe ALL (Orders + Sales + Ledger)</span>
                        <p className="text-xs text-red-600 mt-0.5">
                          Remove ALL orders, sales data AND manual ledger entries. All panels cleared.
                        </p>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Include linked sales/ledger toggle (only meaningful for scoped wipes) */}
                {(wipeOption === "today" || wipeOption === "date_range" || wipeOption === "month") && (
                  <label
                    className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none"
                    data-testid="wipe-include-sales-toggle"
                  >
                    <input
                      type="checkbox"
                      checked={wipeIncludeSales}
                      onChange={(e) => setWipeIncludeSales(e.target.checked)}
                      className="w-3.5 h-3.5"
                    />
                    <span>
                      Also delete linked <strong>Sales + Ledger + Partner uploads</strong> rows for these orders
                    </span>
                  </label>
                )}

                {/* Server-side preview */}
                <div
                  className="border border-slate-200 bg-slate-50 rounded-md p-3 text-xs"
                  data-testid="wipe-preview"
                >
                  <div className="font-medium text-slate-700 mb-1 flex items-center gap-1.5">
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                    Impact preview
                  </div>
                  {wipePreviewLoading ? (
                    <div className="text-slate-500">Calculating…</div>
                  ) : wipePreviewError ? (
                    <div className="text-rose-600">{wipePreviewError}</div>
                  ) : wipePreview ? (
                    <div className="space-y-1 text-slate-700">
                      <div>
                        <strong className="text-red-700 tabular-nums" data-testid="wipe-preview-orders">
                          {(wipePreview.orders ?? 0).toLocaleString()}
                        </strong>{" "}
                        orders will be deleted {wipePreview.scope ? `(${wipePreview.scope})` : ""}
                      </div>
                      {(wipePreview.sales || wipePreview.ledger_entries || wipePreview.partner_uploads) ? (
                        <div className="text-slate-500">
                          Plus from other collections:
                          {wipePreview.sales ? ` sales ${wipePreview.sales.toLocaleString()} ·` : ""}
                          {wipePreview.ledger_entries ? ` ledger ${wipePreview.ledger_entries.toLocaleString()} ·` : ""}
                          {wipePreview.partner_uploads ? ` partner uploads ${wipePreview.partner_uploads.toLocaleString()} ·` : ""}
                          {wipePreview.advance_payments ? ` advance pay ${wipePreview.advance_payments.toLocaleString()} ·` : ""}
                          {wipePreview.packing_staging ? ` packing ${wipePreview.packing_staging.toLocaleString()}` : ""}
                        </div>
                      ) : null}
                      {wipeIncludeSales && (wipeOption === "today" || wipeOption === "date_range" || wipeOption === "month") && (
                        <div className="text-amber-700">
                          Linked Sales/Ledger/Partner rows tied to these trackings will also be removed.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-slate-500">
                      {wipeOption === "date_range" && "Pick start and end dates to see impact."}
                      {wipeOption === "month" && "Pick month and year to see impact."}
                      {(wipeOption === "today" || wipeOption === "whole" || wipeOption === "whole_with_sales") && "Loading…"}
                    </div>
                  )}
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={resetWipeDialog}>Cancel</Button>
                <Button
                  variant="destructive"
                  onClick={() => setWipeStep(2)}
                  className="gap-2 bg-red-600 hover:bg-red-700"
                  disabled={!wipePreview || wipePreview.orders === 0}
                  data-testid="wipe-continue-btn"
                >
                  <ShieldAlert className="w-4 h-4" />
                  Yes, Continue
                </Button>
              </DialogFooter>
            </>
          ) : (
            /* Step 2: Password Verification */
            <>
              <div className="space-y-4 py-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 font-medium flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" />
                    Security Verification Required
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    Enter today&apos;s date as password in <strong>DDMM</strong> format.
                  </p>
                  <p className="text-xs text-amber-600 mt-1">
                    Example: If today is 15th March, enter <strong>1503</strong>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="wipePassword" className="text-sm font-medium">
                    Enter Password (DDMM):
                  </Label>
                  <Input
                    id="wipePassword"
                    type="password"
                    maxLength={4}
                    placeholder="Enter 4-digit date password"
                    value={wipePassword}
                    onChange={(e) => {
                      setWipePassword(e.target.value.replace(/\D/g, '').slice(0, 4));
                      setWipePasswordError("");
                    }}
                    className={`text-center text-2xl tracking-widest font-mono ${wipePasswordError ? 'border-red-500' : ''}`}
                    autoFocus
                  />
                  {wipePasswordError && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {wipePasswordError}
                    </p>
                  )}
                </div>

                <div className="bg-slate-50 border rounded-lg p-3">
                  <p className="text-xs text-slate-600" data-testid="wipe-step2-summary">
                    <strong>Selected option:</strong>{" "}
                    {wipeOption === "today" && "Today's Upload"}
                    {wipeOption === "date_range" && `Date Range (${
                      wipeDateFrom ? formatDate(parseISO(wipeDateFrom), "dd/MM/yyyy") : "—"
                    } → ${
                      wipeDateTo ? formatDate(parseISO(wipeDateTo), "dd/MM/yyyy") : "—"
                    })`}
                    {wipeOption === "month" && `Monthly (${String(wipeMonth).padStart(2, "0")}/${wipeYear})`}
                    {wipeOption === "whole" && "Wipe Orders Only (full)"}
                    {wipeOption === "whole_with_sales" && "Wipe ALL (Orders + Sales + Ledger)"}
                    {wipePreview?.orders != null && (
                      <span className="ml-2 text-red-600 font-medium">
                        — {wipePreview.orders.toLocaleString()} orders will be deleted
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <DialogFooter className="gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setWipeStep(1);
                    setWipePassword("");
                    setWipePasswordError("");
                  }}
                  disabled={wiping}
                >
                  Back
                </Button>
                <Button 
                  variant="destructive"
                  onClick={verifyWipePassword}
                  disabled={wiping || wipePassword.length !== 4}
                  className="gap-2 bg-red-600 hover:bg-red-700"
                >
                  {wiping ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Wiping...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Confirm Wipe
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Print Dialog with Filter Options */}
      <Dialog open={showPrintDialog} onOpenChange={setShowPrintDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-orange-500" />
              Print Order Cost Sheet
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Quick Print Options */}
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="h-20 flex-col gap-2 border-green-300 hover:bg-green-50"
                onClick={() => handlePrint(false)}
              >
                <Package className="w-6 h-6 text-green-600" />
                <span className="text-sm font-medium">Print All</span>
                <span className="text-xs text-slate-500">{orders.length} orders</span>
              </Button>
              
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  className="h-20 flex-col gap-2 border-purple-300 hover:bg-purple-50"
                  onClick={() => handlePrint(true)}
                >
                  <Filter className="w-6 h-6 text-purple-600" />
                  <span className="text-sm font-medium">Print Filtered</span>
                  <span className="text-xs text-slate-500">{filteredOrders.length} orders</span>
                </Button>
              )}
            </div>

            {/* Show/Hide Header & Summary Cards */}
            <div className="border-t pt-3 space-y-2">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="printShowCompanyName"
                  checked={printShowCompanyName}
                  onChange={(e) => setPrintShowCompanyName(e.target.checked)}
                  className="w-4 h-4 rounded"
                  data-testid="print-show-company-name"
                />
                <label htmlFor="printShowCompanyName" className="text-sm text-slate-700">
                  Show company name title
                </label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="printShowHeader"
                  checked={printShowHeader}
                  onChange={(e) => setPrintShowHeader(e.target.checked)}
                  className="w-4 h-4 rounded"
                  data-testid="print-show-header"
                />
                <label htmlFor="printShowHeader" className="text-sm text-slate-700">
                  Show details (Date, Total Orders, Filters)
                </label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="printShowSummary"
                  checked={printShowSummary}
                  onChange={(e) => setPrintShowSummary(e.target.checked)}
                  className="w-4 h-4 rounded"
                  data-testid="print-show-summary"
                />
                <label htmlFor="printShowSummary" className="text-sm text-slate-700">
                  Show cost summary cards (Medicine, Shipping, Total)
                </label>
              </div>
            </div>

            {/* Column Selection */}
            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-slate-700">Select Columns to Print</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPrintColumns(prev => Object.fromEntries(Object.keys(prev).map(k => [k, true])))}
                    className="text-xs text-blue-600 hover:underline"
                  >All</button>
                  <button
                    onClick={() => setPrintColumns(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])))}
                    className="text-xs text-red-600 hover:underline"
                  >None</button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'client', label: 'Client' },
                  { key: 'date', label: 'Date' },
                  { key: 'service', label: 'Service' },
                  { key: 'tracking', label: 'Tracking' },
                  { key: 'customerName', label: 'Customer Name' },
                  { key: 'country', label: 'Country' },
                  { key: 'product', label: 'Product' },
                  { key: 'qty', label: 'Qty' },
                  { key: 'medicineCost', label: 'Med. Cost' },
                  { key: 'weight', label: 'Weight' },
                  { key: 'shippingCost', label: 'Ship. Cost' },
                  { key: 'total', label: 'Total' },
                  { key: 'errors', label: 'Errors' },
                ].map(col => (
                  <label key={col.key} className="flex items-center gap-2 text-xs cursor-pointer py-1 px-2 rounded hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={printColumns[col.key]}
                      onChange={(e) => setPrintColumns(prev => ({ ...prev, [col.key]: e.target.checked }))}
                      className="w-3.5 h-3.5 rounded"
                      data-testid={`print-col-${col.key}`}
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            </div>
            
            {/* Custom Print Filters */}
            <div className="border-t pt-4">
              <h4 className="text-sm font-semibold text-slate-700 mb-3">Or select specific filters to print:</h4>
              
              <div className="space-y-3">
                {/* Errors Only */}
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="printErrors"
                    checked={printFilters.errorsOnly}
                    onChange={(e) => setPrintFilters(prev => ({ ...prev, errorsOnly: e.target.checked }))}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <label htmlFor="printErrors" className="text-sm flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    Errors Only
                  </label>
                </div>
                
                {/* Service */}
                <div className="flex items-center gap-3">
                  <label className="text-sm w-20">Service:</label>
                  <select
                    value={printFilters.service}
                    onChange={(e) => setPrintFilters(prev => ({ ...prev, service: e.target.value }))}
                    className="flex-1 h-8 px-2 text-sm border border-slate-300 rounded-md"
                  >
                    <option value="">All Services</option>
                    {uniqueServices.map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                
                {/* Country */}
                <div className="flex items-center gap-3">
                  <label className="text-sm w-20">Country:</label>
                  <select
                    value={printFilters.country}
                    onChange={(e) => setPrintFilters(prev => ({ ...prev, country: e.target.value }))}
                    className="flex-1 h-8 px-2 text-sm border border-slate-300 rounded-md"
                  >
                    <option value="">All Countries</option>
                    {uniqueCountries.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                
                {/* Client - uses combined clients list */}
                <div className="flex items-center gap-3">
                  <label className="text-sm w-20">Client:</label>
                  <select
                    value={printFilters.client}
                    onChange={(e) => setPrintFilters(prev => ({ ...prev, client: e.target.value }))}
                    className="flex-1 h-8 px-2 text-sm border border-slate-300 rounded-md"
                    data-testid="print-client-filter"
                  >
                    <option value="">All Accounts</option>
                    {[...new Set([...uniqueClients, ...allLedgerClients])].sort().map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                
                {/* Min Cost */}
                <div className="flex items-center gap-3">
                  <label className="text-sm w-20">Cost ≥ ₹:</label>
                  <Input
                    type="number"
                    value={printFilters.costMin}
                    onChange={(e) => setPrintFilters(prev => ({ ...prev, costMin: e.target.value }))}
                    placeholder="e.g., 1000"
                    className="flex-1 h-8"
                  />
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowPrintDialog(false);
                setPrintFilters({ errorsOnly: false, service: "", country: "", client: "", costMin: "" });
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handlePrint(false)}
              className="gap-2 bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Printer className="w-4 h-4" />
              Print with Selected Filters
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Date Conflict Dialog */}
      <Dialog open={showDateConflictDialog} onOpenChange={setShowDateConflictDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-amber-600 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Date Conflict Detected
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                The uploaded file contains orders with dates that already exist in the database.
              </p>
              {dateConflictData && (
                <div className="mt-3 space-y-2">
                  <div className="text-sm">
                    <span className="font-medium text-slate-700">File date range: </span>
                    <span className="text-blue-600 font-mono">
                      {dateConflictData.file_date_range?.from} to {dateConflictData.file_date_range?.to}
                    </span>
                    <span className="text-slate-500 ml-2">({dateConflictData.file_date_range?.count} unique dates)</span>
                  </div>
                  <div className="text-sm">
                    <span className="font-medium text-slate-700">Conflicting dates: </span>
                    <span className="text-red-600 font-medium">
                      {dateConflictData.conflicting_dates?.length || 0} dates
                    </span>
                  </div>
                  {dateConflictData.conflicting_dates_formatted?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {dateConflictData.conflicting_dates_formatted.slice(0, 10).map((d, i) => (
                        <span key={i} className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded font-mono">
                          {d}
                        </span>
                      ))}
                      {dateConflictData.conflicting_dates_formatted.length > 10 && (
                        <span className="px-2 py-0.5 text-slate-500 text-xs">
                          +{dateConflictData.conflicting_dates_formatted.length - 10} more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <p className="text-sm text-slate-600">
              What would you like to do?
            </p>
          </div>
          
          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => {
              setShowDateConflictDialog(false);
              setPendingUploadFile(null);
              setDateConflictData(null);
            }}>
              Cancel
            </Button>
            <Button variant="outline" className="text-blue-600 border-blue-300 hover:bg-blue-50" onClick={handleAddNewOnly}>
              Add New Only
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handleOverwriteRequest}>
              Overwrite Existing
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Dialog for Overwrite */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-red-600 flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Password Required
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              To overwrite existing orders, please enter the password.
            </p>
            <p className="text-xs text-slate-400">
              Hint: Today&apos;s date in DDMM format
            </p>
            <Input
              type="password"
              value={overwritePassword}
              onChange={(e) => setOverwritePassword(e.target.value)}
              placeholder="Enter password (DDMM)"
              className="font-mono text-center text-lg tracking-widest"
              maxLength={4}
              onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
            />
          </div>
          
          <div className="flex gap-3 justify-end pt-2 border-t">
            <Button variant="outline" onClick={() => {
              setShowPasswordDialog(false);
              setOverwritePassword("");
              setPendingUploadFile(null);
              setDateConflictData(null);
            }}>
              Cancel
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={handlePasswordSubmit}>
              Confirm Overwrite
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Tracking Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-orange-600 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6" />
              Duplicate Tracking Numbers Found
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <p className="text-sm text-orange-800">
                <strong>{duplicateData?.duplicate_count || 0}</strong> tracking numbers from this upload already exist in the database.
              </p>
              
              {duplicateData?.duplicates && duplicateData.duplicates.length > 0 && (
                <div className="mt-3 max-h-48 overflow-y-auto space-y-2">
                  {duplicateData.duplicates.slice(0, 10).map((dup, i) => (
                    <div key={i} className="bg-white rounded p-2 border border-orange-100">
                      <div className="font-mono text-sm text-orange-700">{dup.tracking}</div>
                      <div className="text-xs text-slate-500 mt-1">
                        Already exists with {dup.existing_count} record(s)
                        {dup.existing_records?.[0]?.client_code && (
                          <span> • Client: {dup.existing_records[0].client_code}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {duplicateData.duplicates.length > 10 && (
                    <div className="text-sm text-slate-500 text-center py-2">
                      ... and {duplicateData.duplicates.length - 10} more duplicates
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <p className="text-sm text-slate-600">
              What would you like to do with these duplicates?
            </p>
          </div>
          
          <div className="flex gap-2 justify-end pt-2 border-t flex-wrap">
            <Button variant="outline" onClick={handleCancelDuplicates}>
              Cancel Upload
            </Button>
            <Button 
              variant="outline" 
              className="text-green-600 border-green-300 hover:bg-green-50" 
              onClick={handleSkipDuplicates}
              title="Save only new tracking numbers, skip existing ones"
            >
              Skip Duplicates
            </Button>
            <Button 
              className="bg-orange-600 hover:bg-orange-700 text-white" 
              onClick={handleSaveWithDuplicates}
              title="Replace existing records with new data"
            >
              Replace Existing
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Weight Source Dialog */}
      <Dialog open={showWeightSourceDialog} onOpenChange={setShowWeightSourceDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="w-5 h-5 text-cyan-600" />
              Weight Source Selection
            </DialogTitle>
            <DialogDescription>
              Choose where to fetch weight data from. The packing list has {packingListData ? Object.keys(packingListData).length : 0} parcels loaded.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <RadioGroup value={weightSource} onValueChange={setWeightSource}>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer">
                <RadioGroupItem value="price_list" id="weight_price" />
                <Label htmlFor="weight_price" className="flex-1 cursor-pointer">
                  <div className="font-medium">Price List (Default)</div>
                  <div className="text-sm text-slate-500">Calculate weight from product weight × quantity</div>
                </Label>
              </div>
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-slate-50 cursor-pointer border-cyan-200 bg-cyan-50/50">
                <RadioGroupItem value="packing_list" id="weight_packing" />
                <Label htmlFor="weight_packing" className="flex-1 cursor-pointer">
                  <div className="font-medium text-cyan-700">Packing List (Actual Weight)</div>
                  <div className="text-sm text-cyan-600">Use actual parcel weight from packing sheet</div>
                </Label>
              </div>
            </RadioGroup>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
              <p className="font-medium text-amber-800">Product Sync & Fallback</p>
              <p className="text-amber-700 mt-1">
                <strong>1.</strong> If a parcel in Packing List has more products than in Orders, missing products will be added.<br/>
                <strong>2.</strong> If a product name from the Order Sheet is not found in the Price List, the system will use the product name from the Packing List instead.
              </p>
            </div>
            
            {packingListFile && (
              <div className="bg-slate-100 rounded-lg p-3 text-sm">
                <p className="font-medium text-slate-700">Loaded Packing List:</p>
                <p className="text-slate-600">{packingListFile}</p>
                <p className="text-slate-500 mt-1">{packingListData ? Object.keys(packingListData).length : 0} parcels indexed by tracking number</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2 flex-col sm:flex-row">
            <Button variant="outline" onClick={() => setShowWeightSourceDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSyncPackingProducts}
              className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
              disabled={loading}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Package className="w-4 h-4" />}
              Sync Products & Apply
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DP Client Auto Sales Generation Results Dialog */}
      <Dialog open={showDPSalesDialog} onOpenChange={setShowDPSalesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-600" />
              DP Client Sales Generation Results
            </DialogTitle>
          </DialogHeader>
          
          {dpSalesResult && (
            <div className="space-y-4 py-4">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-700">{dpSalesResult.generated || 0}</div>
                  <div className="text-sm text-green-600">Sales Generated</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-amber-700">{dpSalesResult.skipped_existing || 0}</div>
                  <div className="text-sm text-amber-600">Already Exists</div>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-red-700">{dpSalesResult.error_count || 0}</div>
                  <div className="text-sm text-red-600">Errors (Skipped)</div>
                </div>
              </div>

              {/* DP Clients Processed */}
              {dpSalesResult.dp_clients_processed?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-700 mb-2">DP Clients Processed</h4>
                  <div className="flex flex-wrap gap-2">
                    {dpSalesResult.dp_clients_processed.map(client => (
                      <span key={client} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                        {client}: {dpSalesResult.sales_by_client?.[client] || 0} sales
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Errors List */}
              {dpSalesResult.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    Missing Prices ({dpSalesResult.errors.length})
                  </h4>
                  <p className="text-xs text-red-600 mb-3">
                    These orders were skipped because product price or shipping rate was not found in DP Client Ledger
                  </p>
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {dpSalesResult.errors.slice(0, 20).map((error, idx) => (
                      <div key={idx} className="bg-white rounded p-2 border border-red-100 text-sm">
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-mono text-red-700">{error.tracking}</span>
                            <span className="mx-2 text-slate-400">•</span>
                            <span className="text-slate-600">{error.client_code}</span>
                          </div>
                          <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded">
                            {error.error_type === 'missing_product_price' ? 'No Product Price' : 'No Shipping Rate'}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {error.error_type === 'missing_product_price' 
                            ? `Product: ${error.product_name}`
                            : `${error.country} / ${error.service} / ${error.weight}kg`
                          }
                        </div>
                      </div>
                    ))}
                    {dpSalesResult.errors.length > 20 && (
                      <div className="text-center text-sm text-slate-500 py-2">
                        ... and {dpSalesResult.errors.length - 20} more errors
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Info box */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-sm">
                <p className="text-slate-600">
                  <strong>Price Source:</strong> Product prices and shipping rates are fetched from each DP Client Ledger. 
                  Make sure DP Clients have their <span className="text-indigo-600 font-medium">Price Lists</span> and 
                  <span className="text-blue-600 font-medium"> Shipping Rates</span> configured in the Ledger panel.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDPSalesDialog(false)}>
              Close
            </Button>
            {dpSalesResult?.generated > 0 && (
              <Button 
                onClick={() => window.open('/sales', '_blank')}
                className="bg-green-600 hover:bg-green-700 text-white gap-2"
              >
                <DollarSign className="w-4 h-4" />
                View Sales Panel
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-Upload Summary Dialog */}
      <UploadSummaryDialog
        open={!!uploadSummaryResult}
        onClose={() => setUploadSummaryResult(null)}
        result={uploadSummaryResult}
        token={token}
        onRefresh={handleRefresh}
      />

      {/* Inline-edit password gate */}
      <Dialog
        open={!!pendingEditRow}
        onOpenChange={(v) => {
          if (!v) {
            setPendingEditRow(null);
            setEditPwdInput("");
            // After the modal closes, make sure focus doesn't snap back to
            // any underlying page input (search box, etc.).
            requestAnimationFrame(() => {
              if (document.activeElement && document.activeElement !== document.body) {
                document.activeElement.blur?.();
              }
            });
          }
        }}
      >
        <DialogContent
          className="max-w-md"
          data-testid="inline-edit-pwd-dialog"
          // Stop key events from propagating to the page below (prevents
          // password keystrokes leaking into the search field if focus
          // hasn't fully transferred yet).
          onKeyDownCapture={(e) => { e.stopPropagation(); }}
          onKeyPressCapture={(e) => { e.stopPropagation(); }}
          onClick={(e) => e.stopPropagation()}
          onDoubleClick={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle className="text-indigo-700">Enable row editing</DialogTitle>
            <DialogDescription>
              Enter admin password to enable inline editing for this session. You won't be asked again until the page reloads.
            </DialogDescription>
          </DialogHeader>
          {pendingEditRow && (
            <div className="text-xs bg-slate-50 border rounded p-2 space-y-0.5">
              <div><span className="font-semibold">Tracking:</span> {pendingEditRow.tracking || "—"}</div>
              <div><span className="font-semibold">Client:</span> {pendingEditRow.client_code || "—"}</div>
              <div><span className="font-semibold">Product:</span> {pendingEditRow.product || "—"}</div>
            </div>
          )}
          <input
            type="password"
            name="inline-edit-admin-pwd-new"
            autoComplete="new-password"
            data-lpignore="true"
            data-form-type="other"
            placeholder="Admin password"
            value={editPwdInput}
            onChange={(e) => setEditPwdInput(e.target.value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter") verifyEditPassword();
            }}
            className="w-full px-3 py-2 text-sm border rounded-md"
            autoFocus
            data-testid="inline-edit-pwd-input"
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setPendingEditRow(null); setEditPwdInput(""); }}>Cancel</Button>
            <Button
              onClick={verifyEditPassword}
              disabled={!editPwdInput || editPwdVerifying}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="inline-edit-pwd-confirm"
            >
              {editPwdVerifying ? "Verifying…" : "Unlock"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EditHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        token={token}
        onReverted={() => handleRefresh()}
      />

      <RowEditorDialog
        row={editingRow}
        open={!!editingRow}
        onClose={() => setEditingRow(null)}
        token={token}
        adminPassword={editAdminPwd}
        onSaved={handleRowSaved}
        lookups={editLookups}
        onProductSearch={onEditProductSearch}
      />
    </div>
  );
};

export default ShipmentCostingPage;
